import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  Share,
  ScrollView,
  Pressable,
  Linking,
  Platform,
  Image,
  ImageBackground,
  FlatList,
  TextInput,
  ActivityIndicator,
  type LayoutChangeEvent,
} from "react-native";
import * as Clipboard from "expo-clipboard";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import MaskedView from "@react-native-masked-view/masked-view";
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import {
  ChevronLeft,
  Heart,
  Globe,
  Building2,
  Video,
  Phone,
  Calendar,
  Shield,
  Medal,
  ChevronDown,
  ChevronUp,
  MapPin,
  Pencil,
  Microscope,
  FlaskConical,
  Info,
  Check,
  BookOpen,
  Stethoscope,
  ArrowLeftRight,
  Share2,
  Wallet,
  Compass,
  Search,
  Activity,
  History,
  MessagesSquare,
  Eye,
  ExternalLink,
  type LucideIcon,
} from "lucide-react-native";
import { useFocusEffect } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  useOrganization,
  useNetworkSiblings,
} from "@louise/providers/hooks/useOrganization";
import {
  useClinicUserInteraction,
  useClinicNotes,
  getUserInteractionKey,
  type ClinicJourneyStatus,
} from "@louise/clinics/hooks/useClinicUserInteraction";
import { upsertUserClinicInteraction } from "@louise/clinics/clinics.api";
import { useSavedClinics } from "@louise/providers/hooks/useSavedClinics";
import { useUserLocation } from "@louise/clinics/hooks/useUserLocation";
import {
  calculateDistance,
  formatDistanceForCountry,
} from "@louise/providers/utils/distance";
import { getOrganizationCountryLabel } from "@louise/providers/countryMetadata";
import type {
  Organization,
  ProviderPricing,
  FinancingOption,
  ProviderOutcomes,
  ProviderSocialLink,
} from "@louise/providers/providers.types";
import type { Clinic } from "@louise/clinics/clinics.types";
import type { ClinicOutcomeMetric } from "@louise/fertility-projections";
import { truncateAtComma } from "@louise/clinics/utils/clinicUtils";
import { EMBER } from "../../../onboarding/theme";
import type { AccessStackParamList } from "../../types";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import ClinicImageGallery from "../../components/clinic/ClinicImageGallery";
import ClinicSuccessChart, {
  PersonalizedEstimate,
  getPredictionInfoSections,
} from "../../components/clinic/ClinicSuccessChart";
import InfoSheet from "../../components/InfoSheet";
import ClinicCapabilitiesCard from "../../components/clinic/ClinicCapabilitiesCard";
import ClinicSentimentCard from "../../components/clinic/ClinicSentimentCard";
import StatusBadge from "../../components/StatusBadge";
import ClinicInsuranceCard, {
  hasClinicInsuranceDetails,
} from "../../components/clinic/ClinicInsuranceCard";
import ClinicInfoEmptyState from "../../components/clinic/ClinicInfoEmptyState";
import ClinicPricingCard, {
  ClinicFinancingCard,
} from "../../components/clinic/ClinicPricingCard";
import ClinicLocationCard from "../../components/clinic/ClinicLocationCard";
import { SocialLinkPills } from "../../components/clinic/SocialLinkPills";
import {
  getClinicDisplayImageUrl,
  getClinicFacilityImages,
} from "../../components/clinic/clinicImagePolicy";
import { getPhoneHref } from "../../components/clinic/clinicPhone";
import ScreenTitle, {
  SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE,
} from "../../components/ScreenTitle";
import {
  ActionBanner,
  Dialog,
  DialogActions,
  DialogBodyText,
  DialogHeader,
  DialogPrimaryButton,
  SectionTitle,
  SubsectionLabel,
  Tag,
  MetaText,
  MoonStatBlock,
  MoonFilterChip,
  haptic,
  playSound,
} from "@louise/moon-ui";
import ViewAllLink from "../../components/ViewAllLink";
import {
  useMainChatOpener,
  useTabBarOverride,
} from "../../TabBarOverrideContext";
import { GradientOrb } from "@src/components/ui/GradientOrb";
import FactSheetTabControl, {
  type FactSheetTab,
} from "../../components/FactSheetTabControl";
import { usePredictedSuccessRate } from "@louise/clinics/prediction/usePredictedSuccessRate";
import { usePatient } from "@louise/patients/hooks/usePatient";
import {
  useCareConnections,
  useCreateCareConnection,
} from "@src/packages/patients/hooks/useCareConnections";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useSmartBack } from "../../hooks/useSmartBack";

const velvetCopper =
  require("@louise/moon-ui/assets/images/velvet-copper-btn.jpg") as number;
// Keep saved clinic state visually tied to the same textured active surface as
// MoonToggle rather than introducing a second filled accent treatment.
const toggleActiveTexture =
  require("@src/apps/moon/assets/images/care-hero-glow-gradient.png") as number;
const heartMaskImage = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="white" d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5z"/></svg>',
)}")`;

const NETWORK_LOGO_URLS: Record<string, string> = {
  kindbody: "https://kindbody.com/wp-content/uploads/2020/01/logo.jpg",
  "spring fertility":
    "https://springfertility.com/wp-content/uploads/2024/05/logo-b1.png",
};
const CLINIC_SHARE_ORIGIN = "https://my.louise.life";

function buildClinicShareUrl(clinicId: string): string {
  return `${CLINIC_SHARE_ORIGIN}/access/clinic/${encodeURIComponent(clinicId)}`;
}

type Props = NativeStackScreenProps<AccessStackParamList, "ClinicDetail">;

type ClinicSectionAnchorId =
  | "successRates"
  | "pricing"
  | "insurance"
  | "financing"
  | "medicalTeam"
  | "takeALook"
  | "services"
  | "whatPeopleSay"
  | "visitInformation"
  | "locationHours"
  | "yourJourney";

// Thin margin wrapper around the shared SectionTitle. ClinicDetail's layout
// doesn't have section-level gap, so the explicit margins are kept here as
// a screen-specific helper. All styling (typography, icon) lives in
// `components/SectionTitle.tsx`.
function SectionHeader({
  label,
  icon: Icon,
  onLayout,
}: {
  label: string;
  icon?: LucideIcon;
  onLayout?: (event: LayoutChangeEvent) => void;
}) {
  return (
    <View onLayout={onLayout} style={{ marginTop: 20, marginBottom: 10 }}>
      <SectionTitle icon={Icon}>{label}</SectionTitle>
    </View>
  );
}

type LanguagePresentation = {
  displayName: string;
  flag: string;
  translationKey?: string;
};

// Provider records contain both human-readable language names and ISO 639-1
// codes. Keep the display consistent without mutating the source data.
const LANGUAGE_PRESENTATIONS: Record<string, LanguagePresentation> = {
  english: { displayName: "English", flag: "🇺🇸", translationKey: "english" },
  en: { displayName: "English", flag: "🇺🇸", translationKey: "english" },
  spanish: { displayName: "Spanish", flag: "🇪🇸", translationKey: "spanish" },
  es: { displayName: "Spanish", flag: "🇪🇸", translationKey: "spanish" },
  french: { displayName: "French", flag: "🇫🇷", translationKey: "french" },
  fr: { displayName: "French", flag: "🇫🇷", translationKey: "french" },
  german: { displayName: "German", flag: "🇩🇪", translationKey: "german" },
  de: { displayName: "German", flag: "🇩🇪", translationKey: "german" },
  italian: { displayName: "Italian", flag: "🇮🇹", translationKey: "italian" },
  it: { displayName: "Italian", flag: "🇮🇹", translationKey: "italian" },
  portuguese: {
    displayName: "Portuguese",
    flag: "🇵🇹",
    translationKey: "portuguese",
  },
  pt: { displayName: "Portuguese", flag: "🇵🇹", translationKey: "portuguese" },
  russian: { displayName: "Russian", flag: "🇷🇺", translationKey: "russian" },
  ru: { displayName: "Russian", flag: "🇷🇺", translationKey: "russian" },
  arabic: { displayName: "Arabic", flag: "🇸🇦", translationKey: "arabic" },
  ar: { displayName: "Arabic", flag: "🇸🇦", translationKey: "arabic" },
  mandarin: { displayName: "Mandarin", flag: "🇨🇳", translationKey: "mandarin" },
  chinese: { displayName: "Chinese", flag: "🇨🇳" },
  zh: { displayName: "Chinese", flag: "🇨🇳" },
  "mandarin chinese": { displayName: "Mandarin Chinese", flag: "🇨🇳" },
  cantonese: { displayName: "Cantonese", flag: "🇭🇰" },
  korean: { displayName: "Korean", flag: "🇰🇷", translationKey: "korean" },
  ko: { displayName: "Korean", flag: "🇰🇷", translationKey: "korean" },
  japanese: { displayName: "Japanese", flag: "🇯🇵", translationKey: "japanese" },
  ja: { displayName: "Japanese", flag: "🇯🇵", translationKey: "japanese" },
  vietnamese: {
    displayName: "Vietnamese",
    flag: "🇻🇳",
    translationKey: "vietnamese",
  },
  vi: { displayName: "Vietnamese", flag: "🇻🇳", translationKey: "vietnamese" },
  hindi: { displayName: "Hindi", flag: "🇮🇳" },
  hi: { displayName: "Hindi", flag: "🇮🇳" },
  tagalog: { displayName: "Tagalog", flag: "🇵🇭" },
  farsi: { displayName: "Farsi", flag: "🇮🇷" },
  fa: { displayName: "Farsi", flag: "🇮🇷" },
  hebrew: { displayName: "Hebrew", flag: "🇮🇱" },
  he: { displayName: "Hebrew", flag: "🇮🇱" },
  polish: { displayName: "Polish", flag: "🇵🇱" },
  pl: { displayName: "Polish", flag: "🇵🇱" },
  punjabi: { displayName: "Punjabi", flag: "🇮🇳" },
  thai: { displayName: "Thai", flag: "🇹🇭" },
  th: { displayName: "Thai", flag: "🇹🇭" },
};

function getLanguagePresentation(language: string, t: TranslationFunction) {
  const normalized = language.trim().toLowerCase();
  const presentation = LANGUAGE_PRESENTATIONS[normalized];

  if (!presentation) {
    return { flag: "🌐", label: language.trim() };
  }

  return {
    flag: presentation.flag,
    label: presentation.translationKey
      ? t(`access.find_care.filters.language.${presentation.translationKey}`)
      : presentation.displayName,
  };
}

function ContentCard({ children }: { children: React.ReactNode }) {
  return (
    <View
      style={{
        ...(GLASS_CARD_STYLE as object),
        padding: 20,
        marginBottom: 12,
      }}
    >
      {children}
    </View>
  );
}

function VisitInfoChip({
  label,
  icon: Icon,
}: {
  label: string;
  icon?: LucideIcon;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: Icon ? 5 : 0,
        backgroundColor: "rgba(255,255,255,0.4)",
        borderWidth: 1,
        borderColor: "rgba(255,255,255,0.5)",
        borderRadius: 8,
        paddingHorizontal: 10,
        paddingVertical: 4,
      }}
    >
      {Icon ? <Icon size={12} color={EMBER.colors.textMid} /> : null}
      <Text
        className="font-dm-sans"
        style={{ fontSize: 12, color: EMBER.colors.text }}
      >
        {label}
      </Text>
    </View>
  );
}

function VisitInfoGroup({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <View style={{ gap: 6 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
        }}
      >
        <Icon size={15} color={EMBER.colors.textMid} />
        <SubsectionLabel>{label}</SubsectionLabel>
      </View>
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 6,
          paddingLeft: 25,
        }}
      >
        {children}
      </View>
    </View>
  );
}

function VisitInformationCard({
  clinic,
  consultationTypes,
  languages,
  contactUrl,
  entityLabel,
}: {
  clinic: Clinic;
  consultationTypes?: Organization["consultationTypes"];
  languages?: string[];
  contactUrl?: string;
  entityLabel: string;
}) {
  const t = useTranslations();
  const hasDetails =
    !!clinic.hasOnSiteLab ||
    (consultationTypes ?? []).length > 0 ||
    (languages ?? []).length > 0 ||
    !!clinic.establishedYear;
  const hasConsultations = (consultationTypes ?? []).length > 0;
  const hasLanguages = (languages ?? []).length > 0;

  const consultationsGroup = hasConsultations ? (
    <VisitInfoGroup
      label={t("access.clinic_detail.facility.consultations")}
      icon={Calendar}
    >
      {consultationTypes!.map((consultationType) => {
        const isTelehealth = consultationType === "telehealth";
        return (
          <VisitInfoChip
            key={consultationType}
            label={
              isTelehealth
                ? t("access.clinic_detail.facility.telehealth")
                : t("access.clinic_detail.facility.in_person")
            }
            icon={isTelehealth ? Video : Building2}
          />
        );
      })}
    </VisitInfoGroup>
  ) : null;

  const languagesGroup = hasLanguages ? (
    <VisitInfoGroup
      label={t("access.clinic_detail.facility.languages")}
      icon={Globe}
    >
      {languages!.map((language) => {
        const presentation = getLanguagePresentation(language, t);
        return (
          <VisitInfoChip
            key={language}
            label={`${presentation.flag} ${presentation.label}`}
          />
        );
      })}
    </VisitInfoGroup>
  ) : null;

  return (
    <ContentCard>
      {hasDetails ? (
        <View style={{ gap: 14 }}>
          {clinic.hasOnSiteLab && (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
              }}
            >
              <Microscope size={15} color={EMBER.colors.textMid} />
              <Text
                className="font-dm-sans"
                style={{ fontSize: 13, color: EMBER.colors.text }}
              >
                {t("access.clinic_detail.facility.onsite_lab")}
              </Text>
            </View>
          )}

          {consultationsGroup}
          {languagesGroup}

          {clinic.establishedYear && (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
              }}
            >
              <Shield size={15} color={EMBER.colors.textMid} />
              <Text
                className="font-dm-sans"
                style={{ fontSize: 13, color: EMBER.colors.text }}
              >
                {t("access.clinic_detail.facility.established", {
                  params: { year: String(clinic.establishedYear) },
                })}
              </Text>
            </View>
          )}
        </View>
      ) : (
        <ClinicInfoEmptyState
          message={t("access.clinic_detail.empty.clinic_details", {
            params: { entity: entityLabel },
          })}
          contactUrl={contactUrl}
        />
      )}
    </ContentCard>
  );
}

// ---------------------------------------------------------------------------
// Reporting source labels
//
// We branch UI copy on `reportedTo` so each rendered outcome cites the body it
// actually reports to. Directory/contact sources are not outcome sources.
// ---------------------------------------------------------------------------

type ReportingSourceInfo = {
  shortLabel: string; // e.g. "CDC data" / "ABM data"
  fullName: string; // e.g. "Centers for Disease Control" / "Agence de la Biomédecine"
  sourceType: NonNullable<Clinic["outcomeSourceType"]>;
  rateHeading: string;
  inlineDisclosure?: string;
  whatThisShows: string;
  dataSource: string;
  nationalAverage: string;
};

type SuccessRateEmptyStateCopy = {
  title: string;
  message: string;
};

const SUPPLEMENTAL_EUROPE_COUNTRIES_WITHOUT_IMPORTED_OUTCOMES = new Set([
  "AL",
  "AM",
  "AT",
  "AZ",
  "BA",
  "BG",
  "BY",
  "CH",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "GE",
  "HR",
  "HU",
  "IE",
  "IS",
  "LT",
  "LU",
  "LV",
  "ME",
  "MK",
  "MT",
  "NL",
  "NO",
  "PL",
  "RO",
  "RS",
  "SE",
  "SI",
  "SK",
  "UA",
]);

const SUPPLEMENTAL_EUROPE_COUNTRY_OUTCOME_NOTE_KEYS: Record<string, string> = {
  AT: "supplemental_europe_at",
  DK: "supplemental_europe_dk",
  NL: "supplemental_europe_nl",
  SE: "supplemental_europe_se",
};

const REDLARA_COUNTRIES_WITHOUT_IMPORTED_OUTCOMES = new Set([
  "AR",
  "BO",
  "BR",
  "CL",
  "CO",
  "CR",
  "CU",
  "DO",
  "EC",
  "GT",
  "HN",
  "MX",
  "NI",
  "PA",
  "PE",
  "PY",
  "SV",
  "UY",
  "VE",
]);

const NETWORK_COMPARISON_AGE_BANDS = new Set(["<35", "35-37", "38-40", ">40"]);

function countryLabel(country: string | undefined | null) {
  if (!country) return "";
  return getOrganizationCountryLabel(country);
}

function hasPositiveOutcomeRates(
  outcomes: Organization["outcomes"] | undefined,
) {
  return (outcomes?.successRates ?? []).some(
    (rate) => typeof rate.rate === "number" && rate.rate > 0,
  );
}

function hasPublishedOutcomeRows(
  outcomes: Organization["outcomes"] | undefined,
) {
  return (outcomes?.successRates ?? []).some(
    (rate) =>
      typeof rate.rate === "number" &&
      rate.rate > 0 &&
      (Boolean(rate.label) ||
        Boolean(rate.treatment) ||
        Boolean(rate.metric) ||
        !["<35", "35-37", "38-40", ">40", "all-ages"].includes(rate.ageBand)),
  );
}

function inferOutcomeSourceType(
  outcomes: Organization["outcomes"] | undefined,
): NonNullable<Clinic["outcomeSourceType"]> {
  if (outcomes?.sourceType) return outcomes.sourceType;
  if (outcomes?.reportedTo === "CDC" || outcomes?.reportedTo === "SART") {
    return "national-mandatory-registry";
  }
  if (
    outcomes?.reportedTo === "HFEA" ||
    outcomes?.reportedTo === "Registro SEF"
  ) {
    return "national-mandatory-registry";
  }
  if (outcomes?.reportedTo === "ABM") {
    return "official-national-publication";
  }
  if (
    outcomes?.reportedTo === "BELRAP" ||
    outcomes?.reportedTo === "ISS Registro PMA" ||
    outcomes?.reportedTo === "YourIVFSuccess / ANZARD"
  ) {
    return "official-national-publication";
  }
  if (outcomes?.reportedTo === "Ontario Fertility Program") {
    return "public-funding-program";
  }
  if (
    outcomes?.reportedTo === "REDLARA" ||
    outcomes?.reportedTo === "CNPMA" ||
    outcomes?.reportedTo === "D.I.R." ||
    outcomes?.reportedTo === "EAIYA"
  ) {
    return "registry-participation";
  }
  return "clinic-self-published";
}

function getReportingSourceInfo(
  outcomes: Organization["outcomes"] | undefined,
  t: TranslationFunction,
): ReportingSourceInfo {
  const sourceType = inferOutcomeSourceType(outcomes);
  const reportedTo = outcomes?.reportedTo;

  const sourceKey =
    reportedTo === "ABM"
      ? "abm"
      : reportedTo === "Ontario Fertility Program"
        ? "ontario"
        : reportedTo === "REDLARA"
          ? "redlara"
          : reportedTo === "HFEA"
            ? "hfea"
            : reportedTo === "Registro SEF"
              ? "sef"
              : reportedTo === "deGynaecoloog/NVOG"
                ? "dutch_ivf"
                : reportedTo === "YourIVFSuccess / ANZARD"
                  ? "anzard"
                  : reportedTo === "BELRAP"
                    ? "belrap"
                    : reportedTo === "D.I.R."
                      ? "dir"
                      : reportedTo === "ISS Registro PMA"
                        ? "iss"
                        : reportedTo === "CNPMA"
                          ? "cnpma"
                          : reportedTo === "EAIYA"
                            ? "eaiya"
                            : sourceType === "clinic-self-published"
                              ? "clinic_published"
                              : "cdc";
  const path = `access.clinic_detail.reporting_sources.${sourceKey}`;

  return {
    shortLabel: t(`${path}.short_label`),
    fullName: t(`${path}.full_name`),
    sourceType,
    rateHeading: t(`${path}.rate_heading`),
    inlineDisclosure:
      sourceKey === "clinic_published"
        ? t(`${path}.inline_disclosure`)
        : undefined,
    whatThisShows: t(`${path}.what_this_shows`),
    dataSource: t(`${path}.data_source`),
    nationalAverage: t(`${path}.national_average`),
  };
}

function successRateEmptyStateCopy(
  t: TranslationFunction,
  titleKey: string,
  bodyKey: string,
  params: Record<string, string> = {},
): SuccessRateEmptyStateCopy {
  return {
    title: t(`access.clinic_detail.success_rate_empty.${titleKey}`),
    message: t(`access.clinic_detail.success_rate_empty.${bodyKey}`, {
      params,
    }),
  };
}

function getSuccessRateEmptyStateCopy(
  org: Organization,
  detailEntityLabel: string,
  t: TranslationFunction,
): SuccessRateEmptyStateCopy {
  const reportedTo = org.outcomes?.reportedTo;
  const sourceType = org.outcomes ? inferOutcomeSourceType(org.outcomes) : null;
  const isRedlaraRecord = Boolean(
    org.externalIds?.redlaraId || org.externalIds?.redlaraUrl,
  );

  if (sourceType === "clinic-self-published") {
    return successRateEmptyStateCopy(t, "no_usable_title", "no_usable_body", {
      entity: detailEntityLabel,
    });
  }

  if (reportedTo === "CDC" || reportedTo === "SART") {
    return successRateEmptyStateCopy(
      t,
      "no_published_title",
      "no_cdc_sart_body",
      { entity: detailEntityLabel },
    );
  }

  if (reportedTo === "ABM") {
    return successRateEmptyStateCopy(t, "no_published_title", "no_abm_body", {
      entity: detailEntityLabel,
    });
  }

  if (reportedTo === "HFEA") {
    return successRateEmptyStateCopy(t, "no_imported_title", "no_hfea_body");
  }

  if (reportedTo === "Registro SEF") {
    return successRateEmptyStateCopy(t, "no_imported_title", "no_sef_body");
  }

  if (reportedTo === "BELRAP") {
    return successRateEmptyStateCopy(
      t,
      "no_comparable_title",
      "no_belrap_body",
    );
  }

  if (reportedTo === "D.I.R.") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_dir_body");
  }

  if (reportedTo === "ISS Registro PMA") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_iss_body");
  }

  if (reportedTo === "Ontario Fertility Program") {
    return successRateEmptyStateCopy(
      t,
      "no_comparable_title",
      "no_ontario_listing_body",
    );
  }

  if (reportedTo === "REDLARA") {
    const country = countryLabel(org.address.country);
    return successRateEmptyStateCopy(
      t,
      "no_comparable_title",
      "no_redlara_country_body",
      { country },
    );
  }

  if (reportedTo === "CNPMA") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_cnpma_body");
  }

  if (reportedTo === "EAIYA") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_eaiya_body");
  }

  if (org.address.country === "CA") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_ca_body");
  }

  if (org.address.country === "MX") {
    if (!isRedlaraRecord) {
      return successRateEmptyStateCopy(t, "no_comparable_title", "no_mx_body");
    }

    return successRateEmptyStateCopy(
      t,
      "no_comparable_title",
      "no_redlara_mexico_body",
    );
  }

  if (
    REDLARA_COUNTRIES_WITHOUT_IMPORTED_OUTCOMES.has(org.address.country) &&
    org.address.country !== "MX" &&
    isRedlaraRecord
  ) {
    const country = countryLabel(org.address.country);
    return successRateEmptyStateCopy(
      t,
      "no_comparable_title",
      "no_redlara_record_country_body",
      { country },
    );
  }

  if (
    REDLARA_COUNTRIES_WITHOUT_IMPORTED_OUTCOMES.has(org.address.country) &&
    org.address.country !== "MX"
  ) {
    const country = countryLabel(org.address.country);
    return successRateEmptyStateCopy(
      t,
      "no_comparable_title",
      "no_country_body",
      {
        country,
      },
    );
  }

  if (org.address.country === "AU") {
    return successRateEmptyStateCopy(t, "no_imported_title", "no_au_body");
  }

  if (org.address.country === "IN") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_in_body");
  }

  if (org.address.country === "ID") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_id_body");
  }

  if (org.address.country === "VN") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_vn_body");
  }

  if (org.address.country === "NG") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_ng_body");
  }

  if (org.address.country === "RU") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_ru_body");
  }

  if (
    [
      "AF",
      "AO",
      "BB",
      "BD",
      "BH",
      "BF",
      "BJ",
      "BS",
      "BW",
      "CI",
      "CM",
      "DZ",
      "EG",
      "ET",
      "GA",
      "GH",
      "IR",
      "IQ",
      "JO",
      "JM",
      "KE",
      "KH",
      "KW",
      "KZ",
      "KY",
      "LA",
      "LB",
      "LK",
      "LY",
      "MA",
      "MD",
      "MG",
      "MM",
      "MN",
      "MU",
      "MV",
      "MZ",
      "NA",
      "NP",
      "OM",
      "PG",
      "PK",
      "PS",
      "QA",
      "RW",
      "SD",
      "SN",
      "SY",
      "TG",
      "TN",
      "TT",
      "TZ",
      "UG",
      "UZ",
      "XK",
      "YE",
      "ZM",
      "ZW",
    ].includes(org.address.country)
  ) {
    const country = countryLabel(org.address.country);
    return successRateEmptyStateCopy(
      t,
      "no_comparable_title",
      "no_country_body",
      {
        country,
      },
    );
  }

  if (org.address.country === "JP") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_jp_body");
  }

  if (org.address.country === "KR") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_kr_body");
  }

  if (org.address.country === "CN") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_cn_body");
  }

  if (org.address.country === "FR") {
    return successRateEmptyStateCopy(t, "no_published_title", "no_fr_body", {
      entity: detailEntityLabel,
    });
  }

  if (org.address.country === "GB") {
    return successRateEmptyStateCopy(t, "no_imported_title", "no_gb_body");
  }

  if (org.address.country === "ES") {
    return successRateEmptyStateCopy(t, "no_imported_title", "no_es_body");
  }

  if (org.address.country === "BE") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_be_body");
  }

  if (org.address.country === "DE") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_de_body");
  }

  if (org.address.country === "IT") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_it_body");
  }

  if (org.address.country === "PT") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_pt_body");
  }

  if (org.address.country === "GR") {
    return successRateEmptyStateCopy(t, "no_comparable_title", "no_gr_body");
  }

  if (
    SUPPLEMENTAL_EUROPE_COUNTRIES_WITHOUT_IMPORTED_OUTCOMES.has(
      org.address.country,
    )
  ) {
    const country = countryLabel(org.address.country);
    return successRateEmptyStateCopy(
      t,
      "no_comparable_title",
      SUPPLEMENTAL_EUROPE_COUNTRY_OUTCOME_NOTE_KEYS[org.address.country] ??
        "supplemental_europe_default",
      { country },
    );
  }

  if (org.address.country === "US") {
    return successRateEmptyStateCopy(t, "no_published_title", "no_us_body", {
      entity: detailEntityLabel,
    });
  }

  return successRateEmptyStateCopy(t, "no_published_title", "no_default_body", {
    entity: detailEntityLabel,
  });
}

// ---------------------------------------------------------------------------
// Compat adapter: Organization → Clinic
// ---------------------------------------------------------------------------

function toClinicCompat(org: Organization): Clinic {
  const hasOutcomeRates = hasPositiveOutcomeRates(org.outcomes);
  const outcomeRates = org.outcomes?.successRates ?? [];
  const primaryOutcome =
    outcomeRates.find((rate) => rate.rate > 0) ?? outcomeRates[0];
  const outcomeMetric = normalizeOutcomeMetric(primaryOutcome);
  const outcomeReportingPopulation = [
    org.outcomes?.reportedTo,
    primaryOutcome?.treatment,
    primaryOutcome?.metric,
    primaryOutcome?.denominator
      ? `Denominator: ${primaryOutcome.denominator}`
      : undefined,
  ]
    .filter(Boolean)
    .join("; ");

  return {
    id: org.id,
    name: org.name,
    headline: org.description ?? org.name,
    address: {
      id: `addr-${org.id}`,
      address1: org.address.line1,
      address2: org.address.line2,
      city: org.address.city,
      state: org.address.state,
      postalCode: org.address.zip,
      country: org.address.country,
    },
    images: getClinicFacilityImages(org.images).map((img, i) => ({
      id: `img-${org.id}-${i}`,
      url: img.url,
      name: img.caption ?? img.type ?? "Photo",
      type: img.type,
    })),
    services: [],
    languages: (org.languages ?? []) as Clinic["languages"],
    clinicType: "boutique" as Clinic["clinicType"],
    conditions: [],
    treatments: (org.treatments ?? []).map(
      (t) => t.name,
    ) as Clinic["treatments"],
    website: org.website,
    phone: org.phone,
    email: org.email,
    bookingUrl: org.bookingUrl,
    location: org.location
      ? { latitude: org.location.lat, longitude: org.location.lng }
      : undefined,
    insuranceAccepted: org.insurance?.accepted,
    insuranceProviders: org.insurance?.carriers ?? org.insurance?.providers,
    financiers: (org.insurance?.financingOptions ?? []) as Clinic["financiers"],
    employeeBenefits: (org.insurance?.employeeBenefits ??
      []) as Clinic["employeeBenefits"],
    consultationTypes: (org.consultationTypes ?? []).map((ct) =>
      ct === "in-person" ? "In person" : "Telehealth",
    ) as Clinic["consultationTypes"],
    patientGroups: (org.patientGroups ?? []) as Clinic["patientGroups"],
    accessibilities: (org.accessibilities ?? []) as Clinic["accessibilities"],
    isFirstConsultationFree: org.isFirstConsultationFree,
    successRates: hasOutcomeRates
      ? {
          under35: outcomeRates.find((s) => s.ageBand === "<35")?.rate,
          between35And37: outcomeRates.find((s) => s.ageBand === "35-37")?.rate,
          between38And40: outcomeRates.find((s) => s.ageBand === "38-40")?.rate,
          over42: outcomeRates.find((s) => s.ageBand === ">40")?.rate,
        }
      : undefined,
    hasReportedAnnualIvfData: hasOutcomeRates,
    nbCyclePerYear: org.outcomes?.cyclesPerYear,
    reportedTo: org.outcomes?.reportedTo,
    outcomeSourceType: org.outcomes
      ? inferOutcomeSourceType(org.outcomes)
      : undefined,
    outcomeSourceUrl: org.outcomes?._source?.url,
    outcomeMetric,
    outcomeReportingPopulation: outcomeReportingPopulation || undefined,
    outcomeDenominatorDescription: primaryOutcome?.denominator,
    lastReportYear: org.outcomes?.reportYear,
    team: (org.team ?? []).map((t) => ({
      name: t.credentials?.length
        ? `${t.name}, ${t.credentials.join(", ")}`
        : t.name,
      title: t.role,
      role: t.role,
      imageUrl: t.imageUrl,
    })),
    hasOnSiteLab: org.outcomes?.hasOnSiteLab,
    establishedYear: org.establishedYear,
    publicNotes: org.description,
    schedule: org.schedule
      ? Object.fromEntries(
          Object.entries(org.schedule).map(([day, hours]) => [
            day,
            hours ? { open: hours.open, close: hours.close } : null,
          ]),
        )
      : undefined,
  } as Clinic;
}

function normalizeOutcomeMetric(
  outcome: ProviderOutcomes["successRates"][number] | undefined,
): ClinicOutcomeMetric {
  const context = [outcome?.metric, outcome?.denominator]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (context.includes("transfer")) return "live_birth_per_transfer";
  if (context.includes("retrieval")) return "live_birth_per_retrieval";
  if (context.includes("cycle") || context.includes("attempt")) {
    return "live_birth_per_treatment_attempt";
  }
  return "unknown";
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

type SuccessRateView = "location" | "network";

// Allows visual review to exercise the real clinic-detail empty state without
// changing provider data. The fixture is intentionally dev-only.
function shouldShowDevEmptyClinicImages() {
  if (!__DEV__ || typeof window === "undefined") return false;
  try {
    return (
      new URLSearchParams(window.location.search).get("dev_clinic_images") ===
      "empty"
    );
  } catch {
    return false;
  }
}

// Allows visual review to exercise the personalized-estimate CTA without
// requiring an authenticated patient profile. The fixture only supplies age;
// it never runs outside the dev build.
function getDevClinicPredictionAge(): number | undefined {
  if (!__DEV__ || typeof window === "undefined") return undefined;
  try {
    return new URLSearchParams(window.location.search).get(
      "dev_clinic_prediction",
    ) === "unadjusted"
      ? 34
      : undefined;
  } catch {
    return undefined;
  }
}

function AnimatedClinicSaveHeart({
  saved,
  onPress,
  size = 28,
  accessibilityLabel,
  testID,
  interactive = true,
  strokeColor = EMBER.colors.warm,
  activeStrokeColor = EMBER.colors.coral,
}: {
  saved: boolean;
  onPress: () => void;
  size?: number;
  accessibilityLabel: string;
  testID?: string;
  interactive?: boolean;
  strokeColor?: string;
  activeStrokeColor?: string;
}) {
  const capturePress = usePressCapture("AnimatedClinicSaveHeart");
  const progress = useSharedValue(saved ? 1 : 0);
  const pulse = useSharedValue(1);
  const previousSaved = useRef(saved);

  useEffect(() => {
    if (previousSaved.current === saved) return;
    previousSaved.current = saved;
    progress.value = withTiming(saved ? 1 : 0, { duration: 220 });
    pulse.value = withSequence(
      withSpring(1.24, { damping: 8, stiffness: 280 }),
      withSpring(1, { damping: 11, stiffness: 240 }),
    );
  }, [progress, pulse, saved]);

  const pulseStyle = useAnimatedStyle(() => ({
    transform: [{ scale: pulse.value }],
  }));
  const fillStyle = useAnimatedStyle(() => ({
    opacity: progress.value,
  }));
  const inactiveOutlineStyle = useAnimatedStyle(() => ({
    opacity: 1 - progress.value,
  }));
  const activeOutlineStyle = useAnimatedStyle(() => ({
    opacity: progress.value,
  }));

  const heartContent = (
    <View
      pointerEvents="none"
      style={{ width: size, height: size, position: "relative" }}
    >
      {Platform.OS === "web" ? (
        <Animated.Image
          source={toggleActiveTexture}
          resizeMode="stretch"
          style={[
            { width: size, height: size },
            fillStyle,
            {
              WebkitMaskImage: heartMaskImage,
              maskImage: heartMaskImage,
              WebkitMaskSize: "100% 100%",
              maskSize: "100% 100%",
              WebkitMaskRepeat: "no-repeat",
              maskRepeat: "no-repeat",
            } as Record<string, unknown>,
          ]}
        />
      ) : (
        <MaskedView
          style={{ position: "absolute", width: size, height: size }}
          maskElement={
            <View
              style={{
                width: size,
                height: size,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Heart size={size} color="#000000" fill="#000000" />
            </View>
          }
        >
          <Animated.Image
            source={toggleActiveTexture}
            resizeMode="stretch"
            style={[{ width: size, height: size }, fillStyle]}
          />
        </MaskedView>
      )}
      <Animated.View
        style={[
          {
            position: "absolute",
            width: size,
            height: size,
          },
          inactiveOutlineStyle,
        ]}
      >
        <Heart
          size={size}
          color={strokeColor}
          fill="transparent"
          strokeWidth={2}
        />
      </Animated.View>
      <Animated.View
        style={[
          {
            position: "absolute",
            width: size,
            height: size,
          },
          activeOutlineStyle,
        ]}
      >
        <Heart
          size={size}
          color={activeStrokeColor}
          fill="transparent"
          strokeWidth={2}
        />
      </Animated.View>
    </View>
  );

  return (
    <Animated.View style={[{ width: size, height: size }, pulseStyle]}>
      {interactive ? (
        <Pressable
          onPress={() => {
            capturePress(pressLabel(accessibilityLabel), "toggle_save_clinic");
            haptic(saved ? "light" : "selection");
            playSound(saved ? "toggle-off" : "toggle-on");
            onPress();
          }}
          accessibilityRole="button"
          accessibilityLabel={accessibilityLabel}
          accessibilityState={{ selected: saved }}
          testID={testID}
          style={({ pressed }) => ({
            width: size,
            height: size,
            opacity: pressed ? 0.72 : 1,
          })}
        >
          {heartContent}
        </Pressable>
      ) : (
        heartContent
      )}
    </Animated.View>
  );
}

function ClinicDetailNavButton({
  accessibilityLabel,
  action,
  children,
  label,
  onPress,
  onPressIn,
  onPressOut,
  primary = false,
  selected = false,
  frosted = false,
  disabled = false,
}: {
  accessibilityLabel: string;
  /** i18n key of the visible copy, kept stable for analytics across locales. */
  action: string;
  children: React.ReactNode;
  label?: string;
  onPress: () => void;
  onPressIn?: () => void;
  onPressOut?: () => void;
  primary?: boolean;
  selected?: boolean;
  frosted?: boolean;
  disabled?: boolean;
}) {
  const capturePress = usePressCapture("ClinicDetailNavButton");
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(label, accessibilityLabel), action);
        onPress();
      }}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ selected, disabled }}
      style={({ pressed }) => ({
        minHeight: 48,
        paddingHorizontal: 12,
        borderRadius: 23,
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        flexDirection: "row",
        backgroundColor: frosted
          ? "rgba(255,247,239,0.16)"
          : primary || selected
            ? "#FFF7EF"
            : "rgba(255,247,239,0.08)",
        borderWidth: 1,
        borderColor: frosted
          ? "rgba(255,247,239,0.42)"
          : primary || selected
            ? "rgba(255,247,239,0.94)"
            : "rgba(255,247,239,0.12)",
        opacity: disabled ? 0.48 : pressed ? 0.72 : 1,
        ...(frosted && Platform.OS === "web"
          ? {
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
            }
          : {}),
      })}
    >
      {({ pressed }) => (
        <>
          {children}
          {label ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 15,
                color: frosted
                  ? "rgba(255,247,239,0.95)"
                  : primary || selected
                    ? "#5A3125"
                    : disabled
                      ? "rgba(255,247,239,0.42)"
                      : "rgba(255,247,239,0.86)",
                fontWeight: "900",
              }}
            >
              {label}
            </Text>
          ) : null}
        </>
      )}
    </Pressable>
  );
}

function ClinicDetailTabBar({
  saved,
  onBack,
  onSave,
  onNote,
  onSchedule,
  onOpenChat,
  scheduleDisabled,
}: {
  saved: boolean;
  onBack: () => void;
  onSave: () => void;
  onNote: () => void;
  onSchedule: () => void;
  onOpenChat: () => void;
  scheduleDisabled: boolean;
}) {
  const insets = useSafeAreaInsets();
  const t = useTranslations();
  const capturePress = usePressCapture("ClinicDetailTabBar");
  const saveLabel = saved
    ? t("access.clinic_detail.journey.saved")
    : t("access.clinic_detail.journey.save");
  const transitionProgress = useSharedValue(0);

  useEffect(() => {
    transitionProgress.value = withTiming(1, {
      duration: 360,
      easing: Easing.out(Easing.cubic),
    });
  }, [transitionProgress]);

  const transitionStyle = useAnimatedStyle(() => ({
    opacity: transitionProgress.value,
    transform: [
      { perspective: 900 },
      {
        rotateX: `${interpolate(transitionProgress.value, [0, 1], [72, 0])}deg`,
      },
    ],
  }));

  return (
    <View
      pointerEvents="box-none"
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        paddingBottom: Math.max(insets.bottom, 12),
      }}
    >
      <Animated.View
        style={[
          {
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
          },
          transitionStyle,
          Platform.OS === "web"
            ? ({ transformOrigin: "center bottom" } as Record<string, unknown>)
            : null,
        ]}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
            backgroundColor: "#3B1A08",
            borderRadius: 999,
            paddingHorizontal: 6,
            paddingVertical: 6,
            shadowColor: "#1F120C",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.25,
            shadowRadius: 16,
            elevation: 8,
            ...(Platform.OS === "web"
              ? ({
                  ...SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE,
                  backdropFilter: "blur(18px)",
                  WebkitBackdropFilter: "blur(18px)",
                } as Record<string, string>)
              : {}),
          }}
        >
          <ClinicDetailNavButton
            accessibilityLabel={t("access.clinic_detail.action_dock.back")}
            action="access.clinic_detail.action_dock.back"
            onPress={onBack}
            label={t("access.clinic_detail.action_dock.back")}
          >
            <ChevronLeft
              size={21}
              color="rgba(255,247,239,0.86)"
              strokeWidth={2}
            />
          </ClinicDetailNavButton>
          <ClinicDetailNavButton
            accessibilityLabel={saveLabel}
            action="toggle_save_clinic"
            onPress={onSave}
            selected={saved}
          >
            <AnimatedClinicSaveHeart
              saved={saved}
              onPress={onSave}
              size={21}
              interactive={false}
              accessibilityLabel={saveLabel}
              strokeColor={saved ? "#5A3125" : "rgba(255,247,239,0.86)"}
              activeStrokeColor="#5A3125"
            />
          </ClinicDetailNavButton>
          <ClinicDetailNavButton
            accessibilityLabel={t("access.clinic_detail.action_dock.note")}
            action="access.clinic_detail.action_dock.note"
            onPress={onNote}
            label={t("access.clinic_detail.action_dock.note")}
          >
            <Pencil size={19} color="rgba(255,247,239,0.86)" strokeWidth={2} />
          </ClinicDetailNavButton>
          <ClinicDetailNavButton
            accessibilityLabel={t(
              scheduleDisabled
                ? "access.clinic_detail.action_dock.website"
                : "access.clinic_detail.action_dock.schedule",
            )}
            action={
              scheduleDisabled
                ? "access.clinic_detail.action_dock.website"
                : "access.clinic_detail.action_dock.schedule"
            }
            onPress={onSchedule}
            label={t(
              scheduleDisabled
                ? "access.clinic_detail.action_dock.website"
                : "access.clinic_detail.action_dock.schedule",
            )}
            primary={!scheduleDisabled}
            frosted={!scheduleDisabled}
            disabled={scheduleDisabled}
          >
            <Calendar
              size={19}
              color={
                scheduleDisabled
                  ? "rgba(255,247,239,0.42)"
                  : "rgba(255,247,239,0.92)"
              }
              strokeWidth={2}
            />
          </ClinicDetailNavButton>
        </View>

        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t("chat.hub.open_chat")),
              "chat.hub.open_chat",
            );
            onOpenChat();
          }}
          accessibilityRole="button"
          accessibilityLabel={t("chat.hub.open_chat")}
          style={({ pressed }) => ({
            width: 58,
            height: 58,
            borderRadius: 29,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(20,20,20,0.92)",
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.25,
            shadowRadius: 16,
            elevation: 8,
            opacity: pressed ? 0.82 : 1,
            transform: [{ scale: pressed ? 0.94 : 1 }],
          })}
        >
          <GradientOrb size={34} />
        </Pressable>
      </Animated.View>
    </View>
  );
}

export default function ClinicDetailScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("ClinicDetailScreen");
  const setTabBarOverride = useTabBarOverride();
  const openMainChat = useMainChatOpener();
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Access",
    fallbackScreen: "FindCare",
  });
  const { clinicId } = route.params;
  const { client, tenantId, userId } = useApiClient();
  const queryClient = useQueryClient();

  const {
    data: org,
    error: orgError,
    isLoading: isLoadingOrganization,
  } = useOrganization(clinicId);
  const primaryContactUrl = org?.bookingUrl || org?.website;
  const { data: userInteraction } = useClinicUserInteraction(clinicId, userId);
  const { location: userLocation, isLoading: isLoadingLocation } =
    useUserLocation();

  const rawNetworkSiblings = useNetworkSiblings(clinicId, org?.network?.name);
  const { data: patient } = usePatient();

  // Sort network siblings by distance from user
  const networkSiblings = useMemo(() => {
    if (!userLocation) return rawNetworkSiblings;
    return [...rawNetworkSiblings].sort((a, b) => {
      if (!a.location && !b.location) return 0;
      if (!a.location) return 1;
      if (!b.location) return -1;
      const distA = calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        a.location.lat,
        a.location.lng,
      );
      const distB = calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        b.location.lat,
        b.location.lng,
      );
      return distA - distB;
    });
  }, [rawNetworkSiblings, userLocation]);

  // Determine user's CDC age band for matching sibling success rates
  const userAgeBand = useMemo(() => {
    if (!patient?.dateOfBirth) return null;
    const dob = new Date(patient.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    if (age < 35) return "<35";
    if (age <= 37) return "35-37";
    if (age <= 40) return "38-40";
    return ">40";
  }, [patient?.dateOfBirth]);
  const [militaryExpanded, setMilitaryExpanded] = useState(false);
  const [successInfoVisible, setSuccessInfoVisible] = useState(false);
  const [estimateInfoVisible, setEstimateInfoVisible] = useState(false);
  const [networkInfoVisible, setNetworkInfoVisible] = useState(false);
  const [successRateView, setSuccessRateView] =
    useState<SuccessRateView>("location");
  const [selectedTeamMember, setSelectedTeamMember] =
    useState<TeamMemberType | null>(null);
  const [careTeamMemberAddedId, setCareTeamMemberAddedId] = useState<
    string | null
  >(null);
  const [careTeamAddError, setCareTeamAddError] = useState(false);
  const { data: careConnections = [] } = useCareConnections();
  const createCareConnection = useCreateCareConnection();
  const noteInputRef = useRef<TextInput>(null);
  const clinicScrollRef = useRef<ScrollView | null>(null);
  const sectionOffsets = useRef<Partial<Record<ClinicSectionAnchorId, number>>>(
    {},
  );
  const [activeSection, setActiveSection] =
    useState<ClinicSectionAnchorId>("successRates");

  const registerSection = useCallback(
    (id: ClinicSectionAnchorId) => (event: LayoutChangeEvent) => {
      sectionOffsets.current[id] = event.nativeEvent.layout.y;
    },
    [],
  );

  const scrollToSection = useCallback((id: ClinicSectionAnchorId) => {
    const y = sectionOffsets.current[id];
    if (y == null) return;
    setActiveSection(id);
    clinicScrollRef.current?.scrollTo({
      y: Math.max(0, y - 8),
      animated: true,
    });
  }, []);

  const devEmptyClinicImages = useMemo(
    () => shouldShowDevEmptyClinicImages(),
    [],
  );
  const devClinicPredictionAge = useMemo(() => getDevClinicPredictionAge(), []);
  const clinic = useMemo(() => {
    const compat = org ? toClinicCompat(org) : null;
    if (!compat || !devEmptyClinicImages) return compat;
    return { ...compat, images: [] };
  }, [org, devEmptyClinicImages]);
  const sourceInfo = useMemo(
    () => getReportingSourceInfo(org?.outcomes, t),
    [org?.outcomes, t],
  );
  const emptyClinic = useMemo(
    () =>
      ({
        id: "",
        name: "",
        headline: "",
        address: {
          id: "",
          address1: "",
          city: "",
          postalCode: "",
          country: "",
        },
        images: [],
        services: [],
        languages: [],
        clinicType: "Boutique",
        conditions: [],
        treatments: [],
        consultationTypes: [],
        patientGroups: [],
        accessibilities: [],
      }) as Clinic,
    [],
  );
  const predictionOptions = useMemo(
    () =>
      devClinicPredictionAge
        ? { ageOverride: devClinicPredictionAge }
        : undefined,
    [devClinicPredictionAge],
  );
  const prediction = usePredictedSuccessRate(
    clinic ?? emptyClinic,
    predictionOptions,
  );

  const distance = useMemo(() => {
    if (!userLocation || !org?.location || isLoadingLocation) return null;
    const d = calculateDistance(
      userLocation.latitude,
      userLocation.longitude,
      org.location.lat,
      org.location.lng,
    );
    return formatDistanceForCountry(d, org.address.country);
  }, [userLocation, org?.location, org?.address.country, isLoadingLocation]);

  const mergedPricing = useMemo((): ProviderPricing | null => {
    if (!org) return null;
    const insuranceFinancing: FinancingOption[] = (
      org.insurance?.financingOptions ?? []
    ).map((name) => ({ name, description: name }));
    if (org.pricing) {
      const existing = org.pricing.financingOptions ?? [];
      const existingNames = new Set(existing.map((f) => f.name));
      const extras = insuranceFinancing.filter(
        (f) => !existingNames.has(f.name),
      );
      if (extras.length > 0) {
        return { ...org.pricing, financingOptions: [...existing, ...extras] };
      }
      return org.pricing;
    }
    if (insuranceFinancing.length > 0) {
      return { packages: [], financingOptions: insuranceFinancing };
    }
    return null;
  }, [org]);

  const isLiked = userInteraction?.isLiked ?? false;
  const { syncFromApi, syncNotedFromApi } = useSavedClinics();
  const {
    note,
    setNote,
    status: journeyStatus,
    setStatus: setJourneyStatus,
    handleBlur,
    saveState: journeySaveState,
  } = useClinicNotes(clinicId, userId || "", userInteraction);
  const [shareCopied, setShareCopied] = useState(false);

  const handleShareClinic = async () => {
    const shareUrl = buildClinicShareUrl(clinicId);

    try {
      await Clipboard.setStringAsync(shareUrl);
      setShareCopied(true);
      haptic("success");
      setTimeout(() => setShareCopied(false), 2400);
    } catch {
      // Clipboard access can be unavailable in restricted web contexts; the
      // native share sheet below still gives users another way to send it.
    }

    if (Platform.OS !== "web") {
      try {
        await Share.share({
          message: shareUrl,
          url: shareUrl,
          title: org?.name ?? clinicId,
        });
      } catch {
        // User cancelled the native share sheet — the copied link remains valid.
      }
    }
  };

  useEffect(() => {
    if (userInteraction !== undefined) {
      syncFromApi(clinicId, userInteraction?.isLiked ?? false);
      syncNotedFromApi(
        clinicId,
        !!(userInteraction?.notes && userInteraction.notes.trim().length > 0),
      );
    }
  }, [clinicId, userInteraction, syncFromApi, syncNotedFromApi]);

  const likeMutation = useMutation({
    // Retry transient failures (e.g. a 401 racing a Stytch token refresh)
    // before the onError rollback flips the heart back, which would otherwise
    // read as a flicker.
    retry: 2,
    retryDelay: (attempt) => 300 * (attempt + 1),
    // The target value is passed in as a variable rather than derived from
    // `isLiked` here: onMutate's optimistic setQueryData flips `isLiked`
    // before mutationFn runs, so `!isLiked` would invert back to the wrong
    // value and persist the opposite of what the user tapped.
    mutationFn: (nextLiked: boolean) =>
      upsertUserClinicInteraction(client, tenantId, clinicId, {
        isLiked: nextLiked,
        notes: userInteraction?.notes,
      }),
    onMutate: async (nextLiked: boolean) => {
      await queryClient.cancelQueries({
        queryKey: getUserInteractionKey(clinicId),
      });
      const previous = queryClient.getQueryData(
        getUserInteractionKey(clinicId),
      );
      queryClient.setQueryData(
        getUserInteractionKey(clinicId),
        (old: Record<string, unknown> | undefined) => ({
          ...old,
          isLiked: nextLiked,
        }),
      );
      syncFromApi(clinicId, nextLiked);
      return { previous };
    },
    onError: (_err, nextLiked, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          getUserInteractionKey(clinicId),
          context.previous,
        );
        syncFromApi(clinicId, !nextLiked);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getUserInteractionKey(clinicId),
      });
    },
  });

  const handleJourneySave = useCallback(() => {
    haptic(isLiked ? "light" : "selection");
    playSound(isLiked ? "toggle-off" : "toggle-on");
    likeMutation.mutate(!isLiked);
  }, [isLiked, likeMutation]);

  const handleClinicBack = useCallback(() => {
    setTabBarOverride(null);
    handleBack();
  }, [handleBack, setTabBarOverride]);

  useFocusEffect(
    useCallback(() => {
      if (!org) {
        setTabBarOverride(null);
        return undefined;
      }

      setTabBarOverride(
        <ClinicDetailTabBar
          saved={isLiked}
          onBack={handleClinicBack}
          onSave={handleJourneySave}
          onNote={() => noteInputRef.current?.focus()}
          onSchedule={() => {
            if (primaryContactUrl) Linking.openURL(primaryContactUrl);
          }}
          onOpenChat={openMainChat}
          scheduleDisabled={!primaryContactUrl}
        />,
      );

      return () => setTabBarOverride(null);
    }, [
      handleClinicBack,
      handleJourneySave,
      isLiked,
      openMainChat,
      org,
      primaryContactUrl,
      setTabBarOverride,
    ]),
  );

  // Best success rates across all network siblings per age group (with source location)
  const networkBestRates = useMemo(() => {
    if (networkSiblings.length === 0) return null;

    const best: Record<string, { rate: number; location: string }> = {};
    let hasSomeData = false;

    for (const sibling of networkSiblings) {
      const city = sibling.address.city || truncateAtComma(sibling.name);
      for (const r of sibling.outcomes?.successRates ?? []) {
        if (
          NETWORK_COMPARISON_AGE_BANDS.has(r.ageBand) &&
          typeof r.rate === "number" &&
          r.rate > 0
        ) {
          hasSomeData = true;
          if (!best[r.ageBand] || r.rate > (best[r.ageBand]?.rate ?? 0)) {
            best[r.ageBand] = { rate: r.rate, location: city };
          }
        }
      }
    }

    if (!hasSomeData) return null;
    return best;
  }, [networkSiblings]);

  const networkName = org?.network?.name;
  const networkLogoUrl = networkName
    ? (NETWORK_LOGO_URLS[networkName.trim().toLowerCase()] ??
      org?.logoUrl ??
      networkSiblings.find((sibling) => sibling.logoUrl)?.logoUrl)
    : undefined;

  // Build a synthetic Clinic with the best rates for the chart component
  const networkBestClinic = useMemo(() => {
    if (!networkBestRates || !clinic) return null;
    return {
      ...clinic,
      name: networkName
        ? t("access.clinic_detail.network.locations_name", {
            params: { network: networkName },
          })
        : t("access.clinic_detail.network.other_locations_name"),
      successRates: {
        under35: networkBestRates["<35"]?.rate,
        between35And37: networkBestRates["35-37"]?.rate,
        between38And40: networkBestRates["38-40"]?.rate,
        over42: networkBestRates[">40"]?.rate,
      },
      hasReportedAnnualIvfData: true,
      nbCyclePerYear: undefined,
    } as Clinic;
  }, [networkBestRates, clinic, networkName, t]);

  // Per-age-band location labels for the chart tooltip
  const networkBestLocationByBand = useMemo(() => {
    if (!networkBestRates) return undefined;
    return {
      under35: networkBestRates["<35"]?.location,
      between35And37: networkBestRates["35-37"]?.location,
      between38And40: networkBestRates["38-40"]?.location,
      over42: networkBestRates[">40"]?.location,
    } as Record<string, string | undefined>;
  }, [networkBestRates]);

  const showingNetworkSuccessRates =
    successRateView === "network" && !!networkBestClinic;

  const clinicSectionAnchors: {
    id: ClinicSectionAnchorId;
    label: string;
    icon: LucideIcon;
  }[] = [
    {
      id: "successRates",
      label: t("access.clinic_detail.section.success_rates"),
      icon: Medal,
    },
    {
      id: "pricing",
      label: t("access.clinic_detail.section.pricing"),
      icon: Wallet,
    },
    {
      id: "insurance",
      label: t("access.clinic_detail.section.accepted_insurance_benefits"),
      icon: Shield,
    },
    {
      id: "financing",
      label: t("access.clinic_detail.section.financing_options"),
      icon: Wallet,
    },
    {
      id: "medicalTeam",
      label: t("access.clinic_detail.section.medical_team", {
        params: { count: String((org?.team ?? []).length) },
      }),
      icon: Stethoscope,
    },
    {
      id: "takeALook",
      label: t("access.clinic_detail.section.take_a_look"),
      icon: Eye,
    },
    {
      id: "services",
      label:
        (org?.address.country ?? "").toUpperCase() === "US"
          ? t("access.clinic_detail.section.services")
          : t("access.clinic_detail.section.treatments"),
      icon: Stethoscope,
    },
    {
      id: "whatPeopleSay",
      label: t("access.clinic_detail.section.what_people_say"),
      icon: MessagesSquare,
    },
    {
      id: "visitInformation",
      label: t("access.clinic_detail.section.visit_information"),
      icon: Info,
    },
    {
      id: "locationHours",
      label: t("access.clinic_detail.section.location_hours"),
      icon: MapPin,
    },
    {
      id: "yourJourney",
      label: t("access.clinic_detail.section.your_journey"),
      icon: Compass,
    },
  ];

  // -- Loading / Error --
  if (orgError || !org || !clinic) {
    return (
      <GlassScreenLayout>
        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{ fontSize: 16, color: EMBER.colors.textLight }}
          >
            {isLoadingOrganization
              ? t("access.clinic_detail.loading")
              : orgError
                ? t("access.clinic_detail.load_failed")
                : t("access.clinic_detail.not_found")}
          </Text>
          {/* Recovery affordance — a dead-end error otherwise has only the tab bar to escape. */}
          {!isLoadingOrganization ? (
            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel(t("access.clinic_detail.back")),
                  "back",
                );
                navigation.goBack();
              }}
              accessibilityRole="button"
              accessibilityLabel={t("access.clinic_detail.back")}
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 4,
                marginTop: 16,
              }}
            >
              <ChevronLeft
                size={18}
                color={EMBER.colors.warm}
                strokeWidth={1.5}
              />
              <Text
                className="font-dm-sans"
                style={{ fontSize: 14, color: EMBER.colors.warm }}
              >
                {t("access.clinic_detail.back")}
              </Text>
            </Pressable>
          ) : null}
        </View>
      </GlassScreenLayout>
    );
  }

  const clinicName = truncateAtComma(org.name);
  const clinicLogoUrl = org.logoUrl
    ? getClinicDisplayImageUrl({ url: org.logoUrl, type: "logo" })
    : null;
  const hasOutcomes = hasPositiveOutcomeRates(org.outcomes);
  const hasTeam = (org.team ?? []).length > 0;
  const hasMilitaryDiscount = !!org.militaryDiscount;
  const showUkCareContext = org.address.country === "GB";
  const detailEntityLabel =
    org.type === "solo-practitioner"
      ? t("access.clinic_detail.entity.provider")
      : t("access.clinic_detail.entity.clinic");
  const successRateEmptyState = getSuccessRateEmptyStateCopy(
    org,
    detailEntityLabel,
    t,
  );
  const facilityEmptyMessage =
    org.type === "solo-practitioner"
      ? t("access.clinic_detail.empty_state.no_facility_provider")
      : t("access.clinic_detail.empty_state.no_facility_clinic");
  // Where the "Get in touch to verify" links in empty states point.
  const contactUrl =
    clinic.website ||
    clinic.bookingUrl ||
    getPhoneHref(clinic.phone, clinic.address.country);
  const successRateTabs: FactSheetTab<SuccessRateView>[] = [
    {
      value: "location",
      label: t("access.clinic_detail.success_tabs.location"),
      icon: MapPin,
    },
    {
      value: "network",
      label: t("access.clinic_detail.success_tabs.network"),
      icon: Globe,
    },
  ];
  const locationSuccessRates =
    hasPublishedOutcomeRows(org?.outcomes) && org?.outcomes ? (
      <ClinicPublishedSuccessRates
        outcomes={org.outcomes}
        clinicName={clinicName}
      />
    ) : clinic.reportedTo === "ABM" ? (
      <View>
        {(org.outcomes?.nationalRank != null ||
          (clinic.nbCyclePerYear != null && clinic.nbCyclePerYear > 0)) && (
          <MetaText style={{ marginTop: 0, marginBottom: 20 }}>
            {org.outcomes?.nationalRank != null &&
            org.outcomes.nationalRankOutOf != null
              ? t("access.cumulative_rate.rank_format", {
                  params: {
                    rank: String(org.outcomes.nationalRank),
                    total: String(org.outcomes.nationalRankOutOf),
                  },
                })
              : null}
            {org.outcomes?.nationalRank != null &&
            org.outcomes.nationalRankOutOf != null &&
            clinic.nbCyclePerYear != null &&
            clinic.nbCyclePerYear > 0
              ? " · "
              : null}
            {clinic.nbCyclePerYear != null && clinic.nbCyclePerYear > 0
              ? t("access.cumulative_rate.volume_unit_year", {
                  params: {
                    count: clinic.nbCyclePerYear.toLocaleString(),
                    year: String(clinic.lastReportYear ?? ""),
                  },
                })
              : null}
          </MetaText>
        )}
        <ClinicSuccessChart
          clinic={clinic}
          prediction={null}
          publishedRates={org.outcomes?.successRates}
          publishedNationalAverage={30}
        />
      </View>
    ) : (
      <ClinicSuccessChart clinic={clinic} prediction={prediction} />
    );

  const addressLine = [
    org.address.line1,
    org.address.line2,
    org.address.city,
    org.address.state,
    org.address.zip,
    countryLabel(org.address.country),
  ]
    .filter(Boolean)
    .join(", ");

  const selectedTeamMemberAlreadyInCareTeam = selectedTeamMember
    ? careConnections.some(
        (connection) =>
          connection.kind === "provider" &&
          connection.name.trim().toLowerCase() ===
            selectedTeamMember.name.trim().toLowerCase(),
      )
    : false;

  const handleAddTeamMemberToCareTeam = async () => {
    if (!selectedTeamMember || createCareConnection.isPending) return;
    setCareTeamAddError(false);
    try {
      await createCareConnection.mutateAsync({
        kind: "provider",
        name: selectedTeamMember.name,
        role: selectedTeamMember.role,
        specialty: selectedTeamMember.specialty ?? null,
        practiceName: clinicName,
        location: addressLine,
        addedVia: "directory",
      });
      setCareTeamMemberAddedId(selectedTeamMember.practitionerId);
    } catch {
      setCareTeamAddError(true);
    }
  };

  const closeTeamMemberDialog = () => {
    if (createCareConnection.isPending) return;
    setSelectedTeamMember(null);
    setCareTeamAddError(false);
  };

  const openMapsAddress = () => {
    const q = encodeURIComponent(addressLine);
    const url =
      Platform.OS === "ios"
        ? `maps:?q=${q}`
        : `https://www.google.com/maps/search/?api=1&query=${q}`;
    Linking.openURL(url);
  };

  return (
    <GlassScreenLayout>
      <ScrollView
        ref={clinicScrollRef}
        testID="clinic-detail-screen"
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 132,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* ── Back + Title ── */}
        <View style={{ marginBottom: 20 }}>
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(
                  route.params?.returnTab ? t("you.shared.back") : "Clinics",
                ),
                "back",
              );
              handleBack();
            }}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
              marginBottom: 8,
            }}
          >
            <ChevronLeft
              size={18}
              color={EMBER.colors.warm}
              strokeWidth={1.5}
            />
            <Text
              className="font-dm-sans"
              style={{ fontSize: 14, color: EMBER.colors.warm }}
            >
              {route.params?.returnTab ? t("you.shared.back") : "Clinics"}
            </Text>
          </Pressable>

          <ScreenTitle>{clinicName}</ScreenTitle>
        </View>

        {/* ━━ HERO — Description + Meta (not in a card) ━━ */}

        {/* Logo */}
        {clinicLogoUrl ? (
          <Image
            source={{ uri: clinicLogoUrl }}
            style={{ width: 100, height: 30, marginBottom: 8 }}
            resizeMode="contain"
          />
        ) : null}

        {/* Address + Distance row */}
        {addressLine ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`Open map for ${addressLine}`}
            onPress={() => {
              // Visible copy is the clinic's street address.
              capturePress(pressLabel("open_clinic_map"));
              openMapsAddress();
            }}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              marginBottom: 6,
            }}
          >
            <MapPin size={13} color={EMBER.colors.textMid} strokeWidth={1.5} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                color: EMBER.colors.textMid,
                flex: 1,
              }}
              numberOfLines={1}
            >
              {distance ? `${distance} away · ` : ""}
              {addressLine}
            </Text>
          </Pressable>
        ) : null}

        <View style={{ height: 8 }} />

        {/* Section anchors — the same glass filter chips used elsewhere in Louise. */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ gap: 8, paddingRight: 8 }}
          style={{ marginBottom: 2 }}
        >
          {clinicSectionAnchors.map((anchor) => (
            <MoonFilterChip
              key={anchor.id}
              label={anchor.label}
              icon={anchor.icon}
              selected={activeSection === anchor.id}
              showCheck={false}
              tone="glass"
              accessibilityLabel={`Jump to ${anchor.label}`}
              onPress={() => {
                haptic("light");
                scrollToSection(anchor.id);
              }}
            />
          ))}
        </ScrollView>

        {/* ━━ SUCCESS RATES ━━ */}
        <SectionHeader
          label={t("access.clinic_detail.section.success_rates")}
          icon={Medal}
          onLayout={registerSection("successRates")}
        />
        {prediction && prediction.adjustments.length > 0 && (
          <>
            <View style={{ flexDirection: "row", gap: 10, marginBottom: 12 }}>
              {/* Left card — clinic rate */}
              <View
                style={{
                  ...(GLASS_CARD_STYLE as object),
                  padding: 16,
                  flex: 1,
                }}
              >
                <MoonStatBlock
                  label={t("access.clinic_detail.stat.clinic_age_rate")}
                  value={`${Math.round(prediction.baseRate)}%`}
                  align="center"
                  layout="label-first"
                  accentColor={EMBER.colors.textMid}
                />
              </View>

              {/* Right card — personalized estimate */}
              <View
                style={{
                  ...(GLASS_CARD_STYLE as object),
                  padding: 16,
                  flex: 1,
                }}
              >
                <Pressable
                  onPress={() => {
                    // Icon-only info affordance with no visible copy.
                    capturePress(pressLabel("open_estimate_info"));
                    setEstimateInfoVisible(true);
                  }}
                  hitSlop={10}
                  style={{ position: "absolute", top: 12, right: 12 }}
                >
                  <Info size={14} color={EMBER.colors.textFaint} />
                </Pressable>
                <MoonStatBlock
                  label={t("access.clinic_detail.stat.personalized_estimate")}
                  value={`~${Math.round(prediction.estimatedRate)}%`}
                  align="center"
                  layout="label-first"
                  accentColor={EMBER.colors.warm}
                />
              </View>
            </View>
          </>
        )}
        {prediction && prediction.adjustments.length === 0 && (
          <ContentCard>
            <PersonalizedEstimate prediction={prediction} />
          </ContentCard>
        )}
        {hasOutcomes && (
          <ContentCard>
            {networkBestClinic ? (
              <>
                <View
                  style={{
                    alignItems: "center",
                    position: "relative",
                    marginBottom: 18,
                  }}
                >
                  <FactSheetTabControl
                    active={successRateView}
                    onChange={setSuccessRateView}
                    tabs={successRateTabs}
                    style={{
                      width: "100%",
                      maxWidth: 300,
                      marginTop: 0,
                    }}
                  />
                  <Pressable
                    onPress={() => {
                      // Icon-only info affordance with no visible copy.
                      capturePress(pressLabel("open_success_rate_info"));
                      if (showingNetworkSuccessRates) {
                        setNetworkInfoVisible(true);
                      } else {
                        setSuccessInfoVisible(true);
                      }
                    }}
                    hitSlop={10}
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 0,
                      padding: 2,
                    }}
                  >
                    <Info size={16} color={EMBER.colors.textFaint} />
                  </Pressable>
                </View>
                {!showingNetworkSuccessRates && sourceInfo.inlineDisclosure ? (
                  <MetaText style={{ marginTop: 0, marginBottom: 12 }}>
                    {sourceInfo.inlineDisclosure}
                  </MetaText>
                ) : null}
                {!showingNetworkSuccessRates &&
                  clinic.reportedTo !== "ABM" &&
                  clinic.nbCyclePerYear != null &&
                  clinic.nbCyclePerYear > 0 && (
                    <MetaText
                      style={{
                        marginTop: 0,
                        marginBottom: 20,
                        textAlign: "center",
                      }}
                    >
                      {t("access.clinic_detail.success_context.cycles_source", {
                        params: {
                          count: clinic.nbCyclePerYear.toLocaleString(),
                          source: sourceInfo.fullName,
                        },
                      })}
                    </MetaText>
                  )}
                {successRateView === "network" ? (
                  <>
                    <MetaText
                      style={{
                        marginTop: 0,
                        marginBottom: 20,
                        textAlign: "center",
                      }}
                    >
                      {t("access.clinic_detail.network.best_reported")}
                    </MetaText>
                    <ClinicSuccessChart
                      clinic={networkBestClinic}
                      prediction={null}
                      locationByBand={networkBestLocationByBand}
                      hideInfo
                    />
                  </>
                ) : (
                  locationSuccessRates
                )}
              </>
            ) : (
              <>
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 2,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      fontWeight: "600",
                      color: EMBER.colors.textMid,
                      flex: 1,
                    }}
                  >
                    {sourceInfo.rateHeading}
                  </Text>
                  <Pressable
                    onPress={() => {
                      // Icon-only info affordance with no visible copy.
                      capturePress(pressLabel("open_success_rate_info"));
                      setSuccessInfoVisible(true);
                    }}
                    hitSlop={10}
                  >
                    <Info size={16} color={EMBER.colors.textFaint} />
                  </Pressable>
                </View>
                {sourceInfo.inlineDisclosure ? (
                  <MetaText style={{ marginTop: 0, marginBottom: 10 }}>
                    {sourceInfo.inlineDisclosure}
                  </MetaText>
                ) : null}
                {clinic.reportedTo !== "ABM" &&
                  clinic.nbCyclePerYear != null &&
                  clinic.nbCyclePerYear > 0 && (
                    <MetaText style={{ marginTop: 0, marginBottom: 20 }}>
                      {clinic.nbCyclePerYear.toLocaleString()} cycles/year ·{" "}
                      {sourceInfo.shortLabel}
                    </MetaText>
                  )}
                {locationSuccessRates}
              </>
            )}
          </ContentCard>
        )}
        {!hasOutcomes && networkBestClinic && (
          <ContentCard>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 4,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "600",
                  color: EMBER.colors.textMid,
                  flex: 1,
                }}
              >
                Highest rates across {networkName ?? "other"} locations
              </Text>
              <Pressable
                onPress={() => {
                  // Icon-only info affordance with no visible copy.
                  capturePress(pressLabel("open_network_rate_info"));
                  setNetworkInfoVisible(true);
                }}
                hitSlop={10}
              >
                <Info size={16} color={EMBER.colors.textFaint} />
              </Pressable>
            </View>
            <MetaText style={{ marginTop: 0, marginBottom: 20 }}>
              This location hasn’t reported its own data
            </MetaText>
            <ClinicSuccessChart
              clinic={networkBestClinic}
              prediction={prediction}
              locationByBand={networkBestLocationByBand}
              hideInfo
            />
          </ContentCard>
        )}
        {!hasOutcomes && !networkBestClinic && (
          <ContentCard>
            <ClinicInfoEmptyState
              title={successRateEmptyState.title}
              message={successRateEmptyState.message}
            />
          </ContentCard>
        )}

        {showUkCareContext && (
          <ContentCard>
            <ClinicUkCareContextCard org={org} />
          </ContentCard>
        )}
        <View onLayout={registerSection("pricing")}>
          <ClinicPricingCard
            pricing={mergedPricing ?? { packages: [] }}
            contactUrl={contactUrl}
            location={`${org.address.city} ${org.address.state ?? ""}`.trim()}
          />
        </View>

        <View onLayout={registerSection("insurance")}>
          <SectionHeader
            label={t(
              "access.clinic_detail.section.accepted_insurance_benefits",
            )}
            icon={Shield}
          />
          {hasClinicInsuranceDetails(clinic) ? (
            <ContentCard>
              <ClinicInsuranceCard clinic={clinic} />
            </ContentCard>
          ) : (
            <ClinicInsuranceCard clinic={clinic} />
          )}
        </View>

        <View onLayout={registerSection("financing")}>
          <SectionHeader
            label={t("access.clinic_detail.section.financing_options")}
            icon={Wallet}
          />
          <ClinicFinancingCard
            options={mergedPricing?.financingOptions ?? []}
            contactUrl={contactUrl}
          />
        </View>

        {hasMilitaryDiscount && org.militaryDiscount && (
          <ContentCard>
            <MilitarySection
              org={org}
              expanded={militaryExpanded}
              onToggle={() => setMilitaryExpanded((v) => !v)}
            />
          </ContentCard>
        )}

        <View onLayout={registerSection("medicalTeam")}>
          <SectionHeader
            label={t("access.clinic_detail.section.medical_team", {
              params: { count: String((org.team ?? []).length) },
            })}
            icon={Stethoscope}
          />
          {hasTeam ? (
            <FlatList
              data={org.team ?? []}
              keyExtractor={(_, i) => `team-${i}`}
              horizontal
              showsHorizontalScrollIndicator={false}
              style={{ marginBottom: 12 }}
              contentContainerStyle={{ gap: 10, paddingBottom: 2 }}
              renderItem={({ item: member }) => (
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(member.name),
                      "clinic_team_member_card",
                    );
                    setSelectedTeamMember(member);
                  }}
                  style={{
                    width: 140,
                    paddingVertical: 14,
                    paddingHorizontal: 10,
                    borderRadius: 14,
                    backgroundColor: "rgba(255,255,255,0.25)",
                    borderWidth: 1,
                    borderColor: "rgba(255,255,255,0.4)",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <View
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 26,
                      overflow: "hidden",
                      backgroundColor: "rgba(255,255,255,0.15)",
                    }}
                  >
                    {member.imageUrl ? (
                      <Image
                        source={{ uri: member.imageUrl }}
                        style={{ width: "100%", height: "100%" }}
                        resizeMode="cover"
                      />
                    ) : (
                      <View
                        style={{
                          width: "100%",
                          height: "100%",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 18,
                            color: EMBER.colors.textFaint,
                          }}
                        >
                          {member.name.charAt(0)}
                        </Text>
                      </View>
                    )}
                  </View>
                  <View style={{ alignItems: "center", gap: 2 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 12,
                        fontWeight: "600",
                        color: EMBER.colors.text,
                        textAlign: "center",
                        lineHeight: 15,
                      }}
                      numberOfLines={2}
                    >
                      {member.name}
                    </Text>
                    {member.credentials?.length ? (
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 10,
                          color: EMBER.colors.textMid,
                          textAlign: "center",
                        }}
                        numberOfLines={1}
                      >
                        {member.credentials.join(", ")}
                      </Text>
                    ) : null}
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 10,
                        color: EMBER.colors.textFaint,
                        textAlign: "center",
                        lineHeight: 13,
                      }}
                      numberOfLines={2}
                    >
                      {member.role}
                    </Text>
                  </View>
                </Pressable>
              )}
            />
          ) : (
            <ClinicInfoEmptyState
              message={t(
                detailEntityLabel === "provider"
                  ? "access.clinic_detail.empty_state.no_medical_team_provider"
                  : "access.clinic_detail.empty_state.no_medical_team_clinic",
              )}
              contactUrl={contactUrl}
            />
          )}
        </View>

        {/* Facility images */}
        <View onLayout={registerSection("takeALook")}>
          <SectionHeader
            label={t("access.clinic_detail.section.take_a_look")}
            icon={Eye}
          />
          {clinic.images.length > 0 ? (
            <ClinicImageGallery clinic={clinic} />
          ) : (
            <ClinicInfoEmptyState
              testID="clinic-images-empty-state"
              message={facilityEmptyMessage}
              contactUrl={contactUrl}
            />
          )}

          {org.socialLinks?.length ? (
            <ClinicSocialLinksCard links={org.socialLinks} />
          ) : null}
        </View>

        {/* ━━ TREATMENTS & CAPABILITIES ━━ */}
        <View onLayout={registerSection("services")}>
          <SectionHeader
            label={
              org.address.country.toUpperCase() === "US"
                ? t("access.clinic_detail.section.services")
                : t("access.clinic_detail.section.treatments")
            }
            icon={Stethoscope}
          />
          <ContentCard>
            <ClinicCapabilitiesCard org={org} />
          </ContentCard>
        </View>

        {/* ━━ WHAT PEOPLE SAY ━━ */}
        <View onLayout={registerSection("whatPeopleSay")}>
          <View
            style={{
              marginTop: 20,
              marginBottom: 10,
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
            }}
          >
            <SectionTitle icon={MessagesSquare}>
              {t("access.clinic_detail.section.what_people_say")}
            </SectionTitle>
            <StatusBadge type="coming-soon" />
          </View>
          <ContentCard>
            <ClinicSentimentCard />
          </ContentCard>
        </View>

        {/* ━━ LOCATION & HOURS ━━ */}
        <View onLayout={registerSection("visitInformation")}>
          <SectionHeader
            label={t("access.clinic_detail.section.visit_information")}
            icon={Info}
          />
          <VisitInformationCard
            clinic={clinic}
            consultationTypes={org.consultationTypes}
            languages={org.languages}
            contactUrl={contactUrl}
            entityLabel={detailEntityLabel}
          />
        </View>

        <View onLayout={registerSection("locationHours")}>
          <SectionHeader
            label={t("access.clinic_detail.section.location_hours")}
            icon={MapPin}
          />
          <ContentCard>
            <ClinicLocationCard clinic={clinic} />
          </ContentCard>
        </View>

        {/* Network siblings */}
        {networkSiblings.length > 0 && (
          <ContentCard>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 14,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "600",
                  color: EMBER.colors.textMid,
                  flex: 1,
                }}
              >
                Other {networkName ?? ""} locations ({networkSiblings.length})
              </Text>
              <ViewAllLink
                onPress={() =>
                  navigation.push("NetworkLocations", {
                    clinicId: org.id,
                    networkName: org.network!.name,
                  })
                }
              />
            </View>
            <FlatList
              data={networkSiblings}
              keyExtractor={(item) => item.id}
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ gap: 10 }}
              ListHeaderComponent={() =>
                networkSiblings.length > 0 ? (
                  <View
                    accessible
                    accessibilityLabel={`${networkName ?? "Network"} logo`}
                    style={{
                      width: 180,
                      height: 110,
                      padding: 14,
                      borderRadius: 14,
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(255,255,255,0.25)",
                      borderWidth: 1,
                      borderColor: "rgba(255,255,255,0.4)",
                    }}
                  >
                    {networkLogoUrl ? (
                      <Image
                        source={{ uri: networkLogoUrl }}
                        resizeMode={
                          networkName?.trim().toLowerCase() === "kindbody"
                            ? "cover"
                            : "contain"
                        }
                        accessibilityIgnoresInvertColors
                        style={{ width: 132, height: 54 }}
                      />
                    ) : (
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 16,
                          fontWeight: "700",
                          lineHeight: 20,
                          color: EMBER.colors.text,
                          textAlign: "center",
                        }}
                        numberOfLines={3}
                      >
                        {networkName ?? "Network locations"}
                      </Text>
                    )}
                  </View>
                ) : null
              }
              renderItem={({ item: sibling }) => {
                const siblingCity = [
                  sibling.address.city,
                  sibling.address.state,
                ]
                  .filter(Boolean)
                  .join(", ");

                // Short name: strip network prefix since the header already says the network
                let shortName = sibling.name;
                if (networkName) {
                  const escaped = networkName.replace(
                    /[.*+?^${}()|[\]\\]/g,
                    "\\$&",
                  );
                  const stripped = sibling.name
                    .replace(new RegExp(`^${escaped}\\s*[-–—]?\\s*`, "i"), "")
                    .trim();
                  if (stripped) shortName = stripped;
                }

                // Distance from user
                let distanceLabel: string | null = null;
                if (userLocation && sibling.location) {
                  const d = calculateDistance(
                    userLocation.latitude,
                    userLocation.longitude,
                    sibling.location.lat,
                    sibling.location.lng,
                  );
                  distanceLabel = formatDistanceForCountry(
                    d,
                    sibling.address.country,
                  );
                }

                // Success rate for user's age band
                const topRate = userAgeBand
                  ? sibling.outcomes?.successRates?.find(
                      (r) =>
                        r.ageBand === userAgeBand &&
                        typeof r.rate === "number" &&
                        r.rate > 0,
                    )
                  : null;

                return (
                  <Pressable
                    onPress={() => {
                      capturePress(
                        pressLabel(shortName),
                        "network_sibling_card",
                      );
                      navigation.push("ClinicDetail", {
                        clinicId: sibling.id,
                      });
                    }}
                    style={{
                      width: 180,
                      paddingVertical: 14,
                      paddingHorizontal: 14,
                      borderRadius: 14,
                      backgroundColor: "rgba(255,255,255,0.25)",
                      borderWidth: 1,
                      borderColor: "rgba(255,255,255,0.4)",
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        fontWeight: "600",
                        color: EMBER.colors.text,
                        lineHeight: 18,
                      }}
                      numberOfLines={2}
                    >
                      {shortName}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 11,
                        color: EMBER.colors.textFaint,
                        marginTop: 4,
                      }}
                      numberOfLines={1}
                    >
                      {siblingCity}
                    </Text>
                    {(distanceLabel || topRate) && (
                      <View style={{ flex: 1, justifyContent: "flex-end" }}>
                        <View
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 8,
                            marginTop: 10,
                          }}
                        >
                          {distanceLabel && (
                            <View
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 3,
                              }}
                            >
                              <MapPin
                                size={11}
                                color={EMBER.colors.textFaint}
                              />
                              <Text
                                className="font-dm-sans"
                                style={{
                                  fontSize: 10,
                                  color: EMBER.colors.textFaint,
                                }}
                              >
                                {distanceLabel}
                              </Text>
                            </View>
                          )}
                          {topRate && (
                            <View
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 3,
                              }}
                            >
                              <Medal size={11} color={EMBER.colors.textFaint} />
                              <Text
                                className="font-dm-sans"
                                style={{
                                  fontSize: 10,
                                  fontWeight: "500",
                                  color: EMBER.colors.textFaint,
                                }}
                              >
                                {Math.round(topRate.rate)}% success
                              </Text>
                            </View>
                          )}
                        </View>
                      </View>
                    )}
                  </Pressable>
                );
              }}
            />
          </ContentCard>
        )}

        {/* ━━ YOUR JOURNEY ━━ */}
        <View onLayout={registerSection("yourJourney")}>
          <SectionHeader
            label={t("access.clinic_detail.section.your_journey")}
            icon={Compass}
          />
          <ContentCard>
            <View style={{ gap: 14 }}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Activity size={15} color={EMBER.colors.textMid} />
                <SubsectionLabel>Status</SubsectionLabel>
              </View>
              <JourneyStatusPills
                value={journeyStatus}
                onChange={setJourneyStatus}
              />
              <View style={{ gap: 6 }}>
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Pencil size={15} color={EMBER.colors.textMid} />
                  <SubsectionLabel>Private note</SubsectionLabel>
                </View>
                <TextInput
                  ref={noteInputRef}
                  testID="clinic-note-input"
                  value={note}
                  onChangeText={setNote}
                  onBlur={handleBlur}
                  placeholder={t(
                    "access.clinic_detail.journey.note_placeholder",
                  )}
                  placeholderTextColor={EMBER.colors.textFaint}
                  multiline
                  textAlignVertical="top"
                  style={{
                    borderRadius: 12,
                    padding: 14,
                    backgroundColor: "rgba(255,255,255,0.25)",
                    borderWidth: 1,
                    borderColor: "rgba(255,255,255,0.3)",
                    minHeight: 112,
                    fontSize: 13,
                    fontFamily: "DMSans_400Regular",
                    color: EMBER.colors.text,
                    lineHeight: 18,
                  }}
                />
                <View accessibilityLiveRegion="polite" style={{ gap: 8 }}>
                  {journeySaveState.status === "saving" && (
                    <View
                      accessible
                      accessibilityLabel={t(
                        "access.clinic_detail.journey.saving",
                      )}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <ActivityIndicator
                        size="small"
                        color={EMBER.colors.textFaint}
                      />
                      <Text
                        className="font-dm-sans"
                        style={{ fontSize: 12, color: EMBER.colors.textFaint }}
                      >
                        {t("access.clinic_detail.journey.saving")}
                      </Text>
                    </View>
                  )}
                </View>
              </View>

              {/* Action buttons */}
              <View style={{ flexDirection: "row", gap: 10, marginTop: 2 }}>
                <View
                  style={{
                    flex: 1,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 7,
                    paddingVertical: 10,
                    borderRadius: 10,
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderWidth: 1,
                    borderColor: "rgba(255,255,255,0.3)",
                    opacity: 0.75,
                  }}
                >
                  <ArrowLeftRight
                    size={14}
                    color={EMBER.colors.textFaint}
                    strokeWidth={2}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: "500",
                      color: EMBER.colors.textFaint,
                    }}
                  >
                    {t("access.clinic_detail.journey.compare")}
                  </Text>
                  <Tag label={t("access.clinic_detail.journey.compare_soon")} />
                </View>
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(
                        t(
                          shareCopied
                            ? "access.clinic_detail.journey.share_copied"
                            : "access.clinic_detail.journey.share",
                        ),
                      ),
                      "access.clinic_detail.journey.share",
                    );
                    handleShareClinic();
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={t(
                    shareCopied
                      ? "access.clinic_detail.journey.share_copied"
                      : "access.clinic_detail.journey.share",
                  )}
                  style={{
                    flex: 1,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 7,
                    paddingVertical: 10,
                    borderRadius: 10,
                    backgroundColor: "rgba(255,255,255,0.3)",
                    borderWidth: 1,
                    borderColor: "rgba(255,255,255,0.4)",
                  }}
                >
                  {shareCopied ? (
                    <Check
                      size={14}
                      color={EMBER.colors.green}
                      strokeWidth={2}
                    />
                  ) : (
                    <Share2
                      size={14}
                      color={EMBER.colors.textMid}
                      strokeWidth={2}
                    />
                  )}
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: "500",
                      color: shareCopied
                        ? EMBER.colors.green
                        : EMBER.colors.textMid,
                    }}
                  >
                    {t(
                      shareCopied
                        ? "access.clinic_detail.journey.share_copied"
                        : "access.clinic_detail.journey.share",
                    )}
                  </Text>
                </Pressable>
              </View>
            </View>
          </ContentCard>
        </View>

        {/* ━━ FOOTER CTA ━━ */}
        <View style={{ marginBottom: 8 }}>
          <Pressable
            onPress={
              primaryContactUrl
                ? () => {
                    capturePress(
                      pressLabel(
                        org.bookingUrl
                          ? t("access.clinic_detail.cta.schedule_consultation")
                          : t("access.clinic_detail.visit_website"),
                      ),
                      "clinic_primary_contact_cta",
                    );
                    Linking.openURL(primaryContactUrl);
                  }
                : undefined
            }
            style={{
              borderRadius: 14,
              overflow: "hidden",
              position: "relative",
              opacity: primaryContactUrl ? 1 : 0.55,
            }}
          >
            <ImageBackground
              source={velvetCopper}
              resizeMode="cover"
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                paddingVertical: 16,
                paddingHorizontal: 16,
                gap: 8,
              }}
            >
              {org.bookingUrl ? (
                <Calendar size={16} color="#FFFFFF" strokeWidth={2} />
              ) : (
                <ExternalLink size={16} color="#FFFFFF" strokeWidth={2} />
              )}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 15,
                  fontWeight: "600",
                  color: "#FFFFFF",
                }}
              >
                {org.bookingUrl
                  ? t("access.clinic_detail.cta.schedule_consultation")
                  : t("access.clinic_detail.visit_website")}
              </Text>
            </ImageBackground>
          </Pressable>
          {!primaryContactUrl && (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                color: EMBER.colors.textFaint,
                textAlign: "center",
                marginTop: 8,
              }}
            >
              Contact details are not available yet.
            </Text>
          )}
        </View>

        {/* ━━ DISCLAIMER ━━ */}
        <View style={{ paddingVertical: 16, gap: 6 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              color: EMBER.colors.textFaint,
              lineHeight: 14,
              textAlign: "center",
            }}
          >
            {
              "Information on this page is sourced from publicly available data on the clinic's website"
            }
            {clinic.reportedTo === "CDC"
              ? " and CDC/SART national reports"
              : clinic.reportedTo === "ABM"
                ? " and Agence de la Biomédecine reports"
                : clinic.reportedTo === "Ontario Fertility Program"
                  ? " and Ontario Fertility Program provider listings"
                  : clinic.reportedTo === "REDLARA"
                    ? " and REDLARA registry/member information"
                    : clinic.outcomeSourceType === "clinic-self-published"
                      ? " and clinic-published outcome materials"
                      : ""}
            . Pricing, insurance, and services may have changed since last
            updated.
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              color: EMBER.colors.textFaint,
              lineHeight: 14,
              textAlign: "center",
            }}
          >
            Last updated:{" "}
            {org._enrichment?.lastEnrichmentRun ??
              org._meta?.updatedAt ??
              "Unknown"}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              color: EMBER.colors.textFaint,
              lineHeight: 14,
              textAlign: "center",
            }}
          >
            Always verify details directly with the clinic before making
            decisions.
          </Text>
        </View>
      </ScrollView>

      <InfoSheet
        visible={successInfoVisible}
        onClose={() => setSuccessInfoVisible(false)}
        title={t("access.clinic_detail.success_modal.title")}
        sections={[
          {
            title: t("access.clinic_detail.success_modal.what_this_shows"),
            body: sourceInfo.whatThisShows,
          },
          {
            title: t("access.clinic_detail.success_modal.data_source"),
            body: sourceInfo.dataSource,
          },
          {
            title: t("access.clinic_detail.success_modal.national_average"),
            body: sourceInfo.nationalAverage,
          },
          {
            body: t("access.clinic_detail.success_modal.disclaimer"),
          },
        ]}
      />

      <InfoSheet
        visible={estimateInfoVisible}
        onClose={() => setEstimateInfoVisible(false)}
        title={t("access.clinic_detail.estimate_modal.title")}
        sections={[
          ...getPredictionInfoSections(t),
          ...(prediction
            ? [
                ...(prediction.adjustments.length > 0
                  ? [
                      {
                        title: t(
                          "access.clinic_detail.estimate_modal_extra.factors_title",
                        ),
                        body: prediction.adjustments
                          .map((adj) => adj.factor)
                          .join(", "),
                      },
                    ]
                  : []),
                {
                  title: t(
                    "access.clinic_detail.estimate_modal_extra.confidence_title",
                  ),
                  body: t(
                    "access.clinic_detail.estimate_modal_extra.confidence_body",
                    {
                      params: {
                        low: String(prediction.confidenceLow),
                        high: String(prediction.confidenceHigh),
                      },
                    },
                  ),
                },
              ]
            : []),
        ]}
      />

      <InfoSheet
        visible={networkInfoVisible}
        onClose={() => setNetworkInfoVisible(false)}
        title={t("access.clinic_detail.network_modal.title")}
        sections={[
          {
            title: t("access.clinic_detail.network_modal.what_this_shows"),
            body: t("access.clinic_detail.network_modal_body.what_this_shows"),
          },
          {
            title: t("access.clinic_detail.network_modal.why_it_matters"),
            body: t("access.clinic_detail.network_modal_body.why_it_matters"),
          },
          {
            body: t("access.clinic_detail.network_modal_body.source_note", {
              params: { source: sourceInfo.fullName },
            }),
          },
        ]}
      />

      {/* Team member detail dialog */}
      <Dialog
        visible={!!selectedTeamMember}
        onClose={closeTeamMemberDialog}
        closeButton="minimal"
        contentStyle={{
          paddingTop: 28,
          paddingHorizontal: 24,
          paddingBottom: 24,
        }}
        testID="clinic-team-member-dialog"
      >
        <TeamMemberModalContent
          member={selectedTeamMember}
          clinicName={clinicName}
          isAddingToCareTeam={createCareConnection.isPending}
          isAddedToCareTeam={
            selectedTeamMemberAlreadyInCareTeam ||
            careTeamMemberAddedId === selectedTeamMember?.practitionerId
          }
          addError={careTeamAddError}
          onAddToCareTeam={handleAddTeamMemberToCareTeam}
        />
      </Dialog>
    </GlassScreenLayout>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ClinicSocialLinksCard({ links }: { links: ProviderSocialLink[] }) {
  const t = useTranslations();
  const visibleLinks = links.filter((link) => link.url);
  if (visibleLinks.length === 0) return null;

  return (
    <ContentCard>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "600",
          color: EMBER.colors.textMid,
          marginBottom: 14,
        }}
      >
        {t("access.clinic_detail.social_channels")}
      </Text>
      <SocialLinkPills links={visibleLinks} />
    </ContentCard>
  );
}

function ClinicUkCareContextCard({ org }: { org: Organization }) {
  const t = useTranslations();
  const isHfeaClinic = Boolean(org.externalIds?.hfeaClinicId);
  const isSupplementalDiagnostics = org.specialties?.includes(
    "uk:supplemental-diagnostics",
  );
  const rows = [
    isHfeaClinic
      ? {
          icon: Shield,
          title: t("access.clinic_detail.uk_context.hfea_title"),
          body: t("access.clinic_detail.uk_context.hfea_body"),
        }
      : isSupplementalDiagnostics
        ? {
            icon: FlaskConical,
            title: t("access.clinic_detail.uk_context.diagnostics_title"),
            body: t("access.clinic_detail.uk_context.diagnostics_body"),
          }
        : {
            icon: Shield,
            title: t("access.clinic_detail.uk_context.provider_title"),
            body: t("access.clinic_detail.uk_context.provider_body"),
          },
    {
      icon: Wallet,
      title: t("access.clinic_detail.uk_context.nhs_title"),
      body: t("access.clinic_detail.uk_context.nhs_body"),
    },
    {
      icon: Calendar,
      title: t("access.clinic_detail.uk_context.referral_title"),
      body: t("access.clinic_detail.uk_context.referral_body"),
    },
    {
      icon: BookOpen,
      title: t("access.clinic_detail.uk_context.consent_title"),
      body: t("access.clinic_detail.uk_context.consent_body"),
    },
  ];

  return (
    <View style={{ gap: 14 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "600",
          color: EMBER.colors.textMid,
        }}
      >
        {t("access.clinic_detail.uk_context.title")}
      </Text>
      <View style={{ gap: 12 }}>
        {rows.map(({ icon: Icon, title, body }) => (
          <View
            key={title}
            style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}
          >
            <Icon
              size={15}
              color={EMBER.colors.textMid}
              style={{ marginTop: 1 }}
            />
            <View style={{ flex: 1, gap: 3 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "600",
                  color: EMBER.colors.text,
                }}
              >
                {title}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 17,
                  color: EMBER.colors.textFaint,
                }}
              >
                {body}
              </Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

function ClinicPublishedSuccessRates({
  outcomes,
  clinicName,
}: {
  outcomes: ProviderOutcomes;
  clinicName: string;
}) {
  const capturePress = usePressCapture("ClinicPublishedSuccessRates");
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const rates = outcomes.successRates.filter(
    (rate) => typeof rate.rate === "number" && rate.rate > 0,
  );

  if (rates.length === 0) return null;

  const maxValue = 100;
  const chartHeight = 128;
  const xAxisHeight = 22;
  const barAreaHeight = chartHeight - xAxisHeight;
  const yAxisLabels = [100, 75, 50, 25, 0];
  const hasNationalAverages = rates.some(
    (rate) =>
      typeof rate.nationalAverage === "number" && rate.nationalAverage > 0,
  );
  const formatRate = (rate: number) =>
    Number.isInteger(rate) ? `${rate}%` : `${rate.toFixed(1)}%`;

  return (
    <View style={{ overflow: "hidden" }}>
      <View style={{ flexDirection: "row", gap: 8, marginBottom: 8 }}>
        <View
          style={{
            height: chartHeight,
            justifyContent: "space-between",
            paddingBottom: xAxisHeight,
          }}
        >
          {yAxisLabels.map((value) => (
            <Text
              key={value}
              className="font-dm-sans"
              style={{
                width: 28,
                fontSize: 10,
                fontWeight: "500",
                color: EMBER.colors.textFaint,
                textAlign: "right",
              }}
            >
              {value}%
            </Text>
          ))}
        </View>

        <View style={{ flex: 1, height: chartHeight, position: "relative" }}>
          <View
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: xAxisHeight,
              justifyContent: "space-between",
            }}
          >
            {yAxisLabels.map((value) => (
              <View
                key={value}
                style={{ height: 1, backgroundColor: "rgba(0,0,0,0.06)" }}
              />
            ))}
          </View>

          <View
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: xAxisHeight,
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "space-around",
              paddingHorizontal: 2,
            }}
          >
            {rates.map((rate, index) => {
              const isSelected = selectedIndex === index;
              const barHeight = Math.max(
                4,
                (Math.min(rate.rate, maxValue) / maxValue) * barAreaHeight,
              );

              return (
                <Pressable
                  key={`${rate.treatment ?? "rate"}-${rate.ageBand}-${rate.rate}-${index}`}
                  onPress={() => {
                    capturePress(
                      pressLabel(rate.label ?? rate.ageBand),
                      "success_rate_bar",
                    );
                    setSelectedIndex(isSelected ? null : index);
                  }}
                  onHoverIn={() => setSelectedIndex(index)}
                  onHoverOut={() => setSelectedIndex(null)}
                  style={{
                    flex: 1,
                    height: "100%",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  {isSelected ? (
                    <View
                      style={{
                        position: "absolute",
                        top: Math.max(
                          0,
                          Math.min(
                            barAreaHeight - barHeight - 34,
                            barAreaHeight - 48,
                          ),
                        ),
                        minWidth: 48,
                        borderRadius: 6,
                        backgroundColor: EMBER.colors.text,
                        paddingHorizontal: 8,
                        paddingVertical: 4,
                        zIndex: 10,
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 10,
                          fontWeight: "700",
                          color: "#FFFFFF",
                          textAlign: "center",
                        }}
                      >
                        {formatRate(rate.rate)}
                      </Text>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 9,
                          color: "rgba(255,255,255,0.68)",
                          textAlign: "center",
                        }}
                        numberOfLines={1}
                      >
                        {hasNationalAverages &&
                        typeof rate.nationalAverage === "number"
                          ? `${rate.nationalAverageLabel ?? "avg"} ${formatRate(rate.nationalAverage)}`
                          : (rate.treatment ?? rate.label ?? rate.ageBand)}
                      </Text>
                    </View>
                  ) : null}

                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 10,
                      fontWeight: "700",
                      color: EMBER.colors.text,
                      marginBottom: 4,
                    }}
                    numberOfLines={1}
                  >
                    {formatRate(rate.rate)}
                  </Text>

                  <View
                    style={{
                      flexDirection: "row",
                      gap: 3,
                      alignItems: "flex-end",
                    }}
                  >
                    {hasNationalAverages &&
                    typeof rate.nationalAverage === "number" &&
                    rate.nationalAverage > 0 ? (
                      <View
                        style={{
                          width: 10,
                          height:
                            (Math.min(rate.nationalAverage, maxValue) /
                              maxValue) *
                            barAreaHeight,
                          borderTopLeftRadius: 2,
                          borderTopRightRadius: 2,
                          backgroundColor: isSelected
                            ? "rgba(0,0,0,0.18)"
                            : "rgba(0,0,0,0.1)",
                        }}
                      />
                    ) : null}
                    <ImageBackground
                      source={velvetCopper}
                      resizeMode="cover"
                      imageStyle={{
                        borderTopLeftRadius: 2,
                        borderTopRightRadius: 2,
                      }}
                      style={{
                        width: hasNationalAverages ? 10 : 12,
                        height: barHeight,
                        borderTopLeftRadius: 2,
                        borderTopRightRadius: 2,
                        overflow: "hidden",
                        opacity: isSelected ? 1 : 0.85,
                      }}
                    />
                  </View>
                </Pressable>
              );
            })}
          </View>

          <View
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: xAxisHeight,
              flexDirection: "row",
              justifyContent: "space-around",
            }}
          >
            {rates.map((rate, index) => (
              <View
                key={`${rate.ageBand}-${rate.label ?? rate.treatment ?? index}-label-${index}`}
                style={{ flex: 1, alignItems: "center", paddingTop: 6 }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 10,
                    fontWeight: "500",
                    color: EMBER.colors.textFaint,
                    textAlign: "center",
                  }}
                  numberOfLines={1}
                >
                  {(rate.label ?? rate.ageBand).replace(/^Age\s+/i, "")}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </View>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: hasNationalAverages ? 16 : 6,
          paddingTop: 10,
          marginTop: 4,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <ImageBackground
            source={velvetCopper}
            resizeMode="cover"
            imageStyle={{ borderRadius: 4 }}
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              overflow: "hidden",
            }}
          />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "600",
              color: EMBER.colors.text,
            }}
          >
            {clinicName}
          </Text>
        </View>
        {hasNationalAverages ? (
          <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <View
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: "rgba(0,0,0,0.12)",
              }}
            />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                fontWeight: "500",
                color: EMBER.colors.textFaint,
              }}
            >
              National average
            </Text>
          </View>
        ) : null}
      </View>
    </View>
  );
}

type TeamMemberType = {
  practitionerId: string;
  name: string;
  credentials?: string[];
  specialty?: string;
  role: string;
  imageUrl?: string;
  socialLinks?: ProviderSocialLink[];
  researchFocus?: string[];
  publicationCount?: number;
};

function TeamMemberModalContent({
  member,
  clinicName,
  isAddingToCareTeam,
  isAddedToCareTeam,
  addError,
  onAddToCareTeam,
}: {
  member: TeamMemberType | null;
  clinicName: string;
  isAddingToCareTeam: boolean;
  isAddedToCareTeam: boolean;
  addError: boolean;
  onAddToCareTeam: () => void;
}) {
  const t = useTranslations();
  if (!member) return null;

  const displayName = member.credentials?.length
    ? `${member.name}, ${member.credentials.join(", ")}`
    : member.name;

  return (
    <View style={{ gap: 16 }}>
      {/* Avatar + name */}
      <View style={{ alignItems: "center", gap: 12 }}>
        <View
          style={{
            width: 72,
            height: 72,
            borderRadius: 36,
            borderWidth: 1.5,
            borderColor: `${EMBER.colors.warm}40`,
            overflow: "hidden",
            backgroundColor: "rgba(255,255,255,0.15)",
          }}
        >
          {member.imageUrl ? (
            <Image
              source={{ uri: member.imageUrl }}
              style={{ width: "100%", height: "100%" }}
              resizeMode="cover"
            />
          ) : (
            <View
              style={{
                width: "100%",
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text style={{ fontSize: 24, color: EMBER.colors.textFaint }}>
                {member.name.charAt(0)}
              </Text>
            </View>
          )}
        </View>
        <DialogHeader
          title={displayName}
          subtitle={member.role}
          style={{ alignItems: "center", width: "100%" }}
          titleStyle={{ textAlign: "center" }}
          subtitleStyle={{ textAlign: "center" }}
        />
        <DialogBodyText style={{ textAlign: "center", marginTop: -4 }}>
          {clinicName}
        </DialogBodyText>
      </View>

      {member.socialLinks?.length ? (
        <SocialLinkPills links={member.socialLinks} />
      ) : null}

      {/* Specialty */}
      {member.specialty && (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <Stethoscope size={14} color={EMBER.colors.textMid} />
          <Text
            className="font-dm-sans"
            style={{ fontSize: 13, color: EMBER.colors.text }}
          >
            {member.specialty}
          </Text>
        </View>
      )}

      {/* Research focus */}
      {member.researchFocus && member.researchFocus.length > 0 && (
        <View style={{ gap: 8 }}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <FlaskConical size={14} color={EMBER.colors.textMid} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "500",
                color: EMBER.colors.text,
              }}
            >
              {t("access.clinic_detail.team_modal.research_focus")}
            </Text>
          </View>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 6,
              paddingLeft: 22,
            }}
          >
            {member.researchFocus.map((area) => (
              <View
                key={area}
                style={{
                  backgroundColor: `${EMBER.colors.warm}12`,
                  borderRadius: 8,
                  paddingHorizontal: 10,
                  paddingVertical: 4,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{ fontSize: 11, color: EMBER.colors.warm }}
                >
                  {area}
                </Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Publications */}
      {member.publicationCount != null && member.publicationCount > 0 && (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <BookOpen size={14} color={EMBER.colors.textMid} />
          <Text
            className="font-dm-sans"
            style={{ fontSize: 13, color: EMBER.colors.text }}
          >
            {t(
              member.publicationCount === 1
                ? "access.clinic_detail.team_modal.publications_one"
                : "access.clinic_detail.team_modal.publications_other",
              { params: { count: String(member.publicationCount) } },
            )}
          </Text>
        </View>
      )}

      {addError ? (
        <ActionBanner
          variant="important"
          message={t("access.clinic_detail.team_modal.add_error")}
        />
      ) : null}

      <DialogActions style={{ marginTop: 0 }}>
        <DialogPrimaryButton
          label={
            isAddedToCareTeam
              ? t("access.clinic_detail.team_modal.added_to_care_team")
              : t("access.clinic_detail.team_modal.add_to_care_team")
          }
          onPress={onAddToCareTeam}
          disabled={isAddedToCareTeam}
          loading={isAddingToCareTeam}
          icon={
            isAddedToCareTeam ? (
              <Check size={16} color="#FFFFFF" strokeWidth={2.2} />
            ) : undefined
          }
          hapticStyle={isAddedToCareTeam ? null : "light"}
          fullWidth
        />
      </DialogActions>
    </View>
  );
}

const JOURNEY_STATUSES: {
  key: ClinicJourneyStatus;
  labelKey: string;
  icon: LucideIcon;
}[] = [
  {
    key: "researching",
    labelKey: "access.clinic_detail.journey.status.researching",
    icon: Search,
  },
  {
    key: "contacted",
    labelKey: "access.clinic_detail.journey.status.contacted",
    icon: Phone,
  },
  {
    key: "consulted",
    labelKey: "access.clinic_detail.journey.status.consulted",
    icon: Stethoscope,
  },
  {
    key: "in-treatment",
    labelKey: "access.clinic_detail.journey.status.in_treatment",
    icon: Activity,
  },
  {
    key: "past-patient",
    labelKey: "access.clinic_detail.journey.status.past_patient",
    icon: History,
  },
];

function JourneyStatusPills({
  value,
  onChange,
}: {
  value: ClinicJourneyStatus | null;
  onChange: (s: ClinicJourneyStatus | null) => void;
}) {
  const t = useTranslations();
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
      {JOURNEY_STATUSES.map(({ key, labelKey, icon }) => {
        const active = value === key;
        return (
          <MoonFilterChip
            key={key}
            label={t(labelKey)}
            icon={icon}
            tone="glass"
            showCheck={false}
            selected={active}
            onPress={() => onChange(active ? null : key)}
          />
        );
      })}
    </View>
  );
}

function MilitarySection({
  org,
  expanded,
  onToggle,
}: {
  org: Organization;
  expanded: boolean;
  onToggle: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("MilitarySection");
  const md = org.militaryDiscount!;
  return (
    <View>
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(expanded ? "collapse_military" : "expand_military"),
          );
          onToggle();
        }}
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: expanded ? 12 : 0,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <Medal size={16} color={EMBER.colors.gold} strokeWidth={2} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 16,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            {t("access.clinic_detail.military.section_title")}
          </Text>
        </View>
        {expanded ? (
          <ChevronUp size={16} color={EMBER.colors.textFaint} />
        ) : (
          <ChevronDown size={16} color={EMBER.colors.textFaint} />
        )}
      </Pressable>

      {!expanded ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: EMBER.colors.textLight,
            marginTop: 6,
          }}
        >
          {md.summary}
        </Text>
      ) : (
        <View style={{ gap: 12 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              fontWeight: "600",
              color: EMBER.colors.text,
            }}
          >
            {md.summary}
          </Text>

          <MilitaryField
            label={t("access.clinic_detail.military.eligibility")}
            value={md.eligibility}
          />
          <MilitaryField
            label={t("access.clinic_detail.military.coverage")}
            value={md.coverage}
          />
          {md.exclusions && (
            <MilitaryField
              label={t("access.clinic_detail.military.exclusions")}
              value={md.exclusions}
              faint
            />
          )}

          {md.additionalPrograms && md.additionalPrograms.length > 0 && (
            <View style={{ gap: 8 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  fontWeight: "600",
                  color: EMBER.colors.textMid,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {t("access.clinic_detail.military.additional_programs")}
              </Text>
              {md.additionalPrograms.map((program, i) => (
                <View
                  key={i}
                  style={{
                    borderRadius: 10,
                    backgroundColor: "rgba(255,255,255,0.35)",
                    padding: 12,
                    gap: 4,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: "600",
                      color: EMBER.colors.text,
                    }}
                  >
                    {program.name}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 11,
                      color: EMBER.colors.textLight,
                      lineHeight: 15,
                    }}
                  >
                    {program.description}
                  </Text>
                  {program.contact ? (
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 11,
                        color: EMBER.colors.warm,
                        fontWeight: "500",
                      }}
                    >
                      {program.contact}
                    </Text>
                  ) : null}
                </View>
              ))}
            </View>
          )}
        </View>
      )}
    </View>
  );
}

function MilitaryField({
  label,
  value,
  faint,
}: {
  label: string;
  value: string;
  faint?: boolean;
}) {
  return (
    <View style={{ gap: 6 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          fontWeight: "600",
          color: EMBER.colors.textMid,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          color: faint ? EMBER.colors.textFaint : EMBER.colors.textLight,
          lineHeight: 17,
        }}
      >
        {value}
      </Text>
    </View>
  );
}
